import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { filterUploadableFiles } from '../../services/uploadErrors.ts';
import {
  MessageSquare,
  Hash,
  Send,
  Paperclip,
  Smile,
  Pin,
  PinOff,
  Search,
  Plus,
  Reply,
  Check,
  CheckCheck,
  MoreVertical,
  X,
  FileText,
  Download,
  Video,
  PhoneCall,
  CheckSquare,
  Users,
  UserPlus,
  Trash2,
  LogOut,
  Loader2,
  ExternalLink,
  UploadCloud,
  Bell,
  BellOff,
  ChevronLeft,
  ChevronDown,
  Camera,
  ZoomIn,
  Copy,
  Edit2,
  ChevronsRight,
  Mic,
  Archive,
  Volume2,
  User as UserIcon,
  Star,
  Calendar,
  Sparkles,
  Clock,
  Sun,
  ListChecks,
  Layers,
  PictureInPicture,
  Info,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useRealtime } from '../../context/RealtimeContext.tsx';
import { useCall } from '../../context/CallContext.tsx';
import { useNotifications } from '../../context/NotificationContext.tsx';
import { useUserProfileModal } from '../../context/UserProfileModalContext.tsx';
import { playIncomingMessageSound, playOutgoingMessageSound } from '../../services/soundEffects.ts';
import { ChatChannel, ChatMessage, User, ChatAttachment, Task } from '../../../shared/types.ts';
import { Avatar } from '../common/Avatar.tsx';
import { RoleBadge } from '../common/Badge.tsx';
import { Modal } from '../common/Modal.tsx';
import { LinkifiedText } from '../common/LinkifiedText.tsx';
import { CreateTaskModal } from '../tasks/CreateTaskModal.tsx';
import { VoiceNotePlayer } from './VoiceNotePlayer.tsx';
import { ForwardMessageModal } from './ForwardMessageModal.tsx';
import { MessageInfoModal } from './MessageInfoModal.tsx';
import { EmojiPickerPopover } from './EmojiPickerPopover.tsx';
import { UserProfileDrawer } from './UserProfileDrawer.tsx';
import { useChatDrafts } from './useChatDrafts.ts';

interface ChatWorkspaceProps {
  initialChannelId?: string | null;
  onPopOut?: (channelId: string) => void;
}

const formatMessageTime = (dateStr?: string | null): string => {
  if (!dateStr) return '';
  try {
    const sanitized = dateStr.includes(' ') && !dateStr.includes('T') ? dateStr.replace(' ', 'T') : dateStr;
    const d = new Date(sanitized);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
};

export const ChatWorkspace: React.FC<ChatWorkspaceProps> = ({ initialChannelId, onPopOut }) => {
  const { token, user } = useAuth();
  const { isConnected, onlineUserIds, joinChannel, leaveChannel, sendTyping, subscribe } = useRealtime();
  const { startDirectCall, startGroupCall } = useCall();
  const { openUserProfile } = useUserProfileModal();

  // Safe online presence check supporting Set, Array, or undefined
  const isUserOnline = (userId?: string | null): boolean => {
    if (!userId || !onlineUserIds) return false;
    if (typeof (onlineUserIds as any).has === 'function') {
      return (onlineUserIds as Set<string>).has(userId);
    }
    if (Array.isArray(onlineUserIds)) {
      return (onlineUserIds as string[]).includes(userId);
    }
    return false;
  };

  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(initialChannelId || null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const messageCacheRef = useRef<Map<string, ChatMessage[]>>(new Map());
  const initialScrollDoneRef = useRef<Record<string, boolean>>({});
  // Chat whose feed has finished its opening scroll to the last message (feed hidden until then)
  const [scrolledChannelId, setScrolledChannelId] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [typingUsers, setTypingUsers] = useState<Record<string, string>>({}); // userId -> name
  const [teamMembers, setTeamMembers] = useState<User[]>([]);

  // Filtering & Search in Conversation List
  const [channelFilter, setChannelFilter] = useState<'all' | 'unread' | 'pinned' | 'unpinned' | 'groups' | 'direct' | 'archived'>('all');
  const [chatSearchQuery, setChatSearchQuery] = useState('');

  // In-Chat Message Search
  const [isMessageSearchOpen, setIsMessageSearchOpen] = useState(false);
  const [messageSearchQuery, setMessageSearchQuery] = useState('');

  // Editing Message
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);

  // Composer text: while editing a message it lives in editText; otherwise it is the opened
  // chat's own draft, so nothing typed in one chat can appear in another.
  const { drafts, getDraft, setDraft } = useChatDrafts(token, user?.id);
  const [editText, setEditText] = useState('');
  const hasAutoOpenedChatRef = useRef(false);
  const inputContent = editingMessage ? editText : selectedChannelId ? drafts[selectedChannelId] || '' : '';
  const setInputContent = (value: string | ((prev: string) => string)) => {
    const current = editingMessage ? editText : getDraft(selectedChannelId);
    const next = typeof value === 'function' ? value(current) : value;
    if (editingMessage) setEditText(next);
    else if (selectedChannelId) setDraft(selectedChannelId, next);
  };
  const [editContent, setEditContent] = useState('');

  // Forwarding Message
  const [forwardingMessage, setForwardingMessage] = useState<ChatMessage | null>(null);

  // Message info (who has read one of my messages, and when)
  const [infoMessage, setInfoMessage] = useState<ChatMessage | null>(null);

  // Deleting Message Modal
  const [deleteModalMessage, setDeleteModalMessage] = useState<ChatMessage | null>(null);

  // Emoji Picker
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);

  // Voice Note Recording
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const voiceTimerRef = useRef<any>(null);

  // Floating Scroll to Bottom
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [unreadWhileScrolledUp, setUnreadWhileScrolledUp] = useState(0);

  // Channel Menu Dropdown
  const [activeChannelMenuId, setActiveChannelMenuId] = useState<string | null>(null);

  // Mobile active action sheet for touched message
  const [activeMobileMessageId, setActiveMobileMessageId] = useState<string | null>(null);

  // Notifications
  const { browserPermission, requestNotificationPermission } = useNotifications();
  const [isPermissionBannerDismissed, setIsPermissionBannerDismissed] = useState(false);

  // Desktop Attachments & Drag-and-Drop
  const [attachedFiles, setAttachedFiles] = useState<ChatAttachment[]>([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [isDraggingOverChat, setIsDraggingOverChat] = useState(false);

  // @mention multi-user tagging autocomplete
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionMatchIndex, setMentionMatchIndex] = useState<number>(-1);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);

  // Group Members Side Panel Drawer
  const [isMembersDrawerOpen, setIsMembersDrawerOpen] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');

  // Create Task from Chat Modal
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [draftTaskTitle, setDraftTaskTitle] = useState('');
  const [draftTaskDescription, setDraftTaskDescription] = useState('');

  // Modals & Member Management
  const [isAddMembersModalOpen, setIsAddMembersModalOpen] = useState(false);
  const [selectedNewMembers, setSelectedNewMembers] = useState<string[]>([]);
  const [addMemberSearchQuery, setAddMemberSearchQuery] = useState('');
  const [isAddingMembers, setIsAddingMembers] = useState(false);

  const [isNewChannelModalOpen, setIsNewChannelModalOpen] = useState(false);
  const [newChannelType, setNewChannelType] = useState<'direct' | 'group'>('direct');
  const [newChannelName, setNewChannelName] = useState('');
  const [newDirectTargetUserId, setNewDirectTargetUserId] = useState('');
  const [newGroupMemberIds, setNewGroupMemberIds] = useState<string[]>([]);
  const [newGroupMemberSearch, setNewGroupMemberSearch] = useState('');
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(false);

  // Quick Invite State for adding colleagues on the fly from chat modals
  const [isQuickInviteOpen, setIsQuickInviteOpen] = useState(false);
  const [quickInviteName, setQuickInviteName] = useState('');
  const [quickInviteEmail, setQuickInviteEmail] = useState('');
  const [isSubmittingQuickInvite, setIsSubmittingQuickInvite] = useState(false);
  const [quickInviteError, setQuickInviteError] = useState<string | null>(null);

  // ----------------- CLICKUP CHAT VALUE ADDITIONS -----------------
  // 1. Header Tabs View Mode (Chat | Calendar | Tasks)
  const [activeHeaderTab, setActiveHeaderTab] = useState<'chat' | 'calendar' | 'tasks'>('chat');

  // 2. Docked Side-by-Side User Profile Drawer
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
  const [profileDrawerUser, setProfileDrawerUser] = useState<User | null>(null);

  // 3. Slash Commands Popup (/task, /call, /meet, /poll, /table, /remind)
  const [isSlashMenuOpen, setIsSlashMenuOpen] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [selectedSlashIndex, setSelectedSlashIndex] = useState(0);

  // 4. Scheduled Messages (Send Later dropdown)
  const [scheduledSendTime, setScheduledSendTime] = useState<string | null>(null);
  const [isScheduleMenuOpen, setIsScheduleMenuOpen] = useState(false);

  // 5. Threaded Replies Panel
  const [isThreadViewOpen, setIsThreadViewOpen] = useState(false);
  const [activeThreadMessage, setActiveThreadMessage] = useState<ChatMessage | null>(null);

  // 6. Channel Tasks & Meetings Embedded View Data
  const [channelTasks, setChannelTasks] = useState<Task[]>([]);
  const [isLoadingChannelTasks, setIsLoadingChannelTasks] = useState(false);
  const [taskStatusFilter, setTaskStatusFilter] = useState<'all' | 'todo' | 'in_progress' | 'done'>('all');
  const [channelMeetings, setChannelMeetings] = useState<any[]>([]);
  const [isLoadingChannelMeetings, setIsLoadingChannelMeetings] = useState(false);

  // 7. Schedule Meeting / 1-on-1 Modal
  const [isScheduleMeetingModalOpen, setIsScheduleMeetingModalOpen] = useState(false);
  const [meetingModalTitle, setMeetingModalTitle] = useState('');
  const [meetingModalDate, setMeetingModalDate] = useState('');
  const [meetingModalTime, setMeetingModalTime] = useState('10:00');
  const [meetingModalDuration, setMeetingModalDuration] = useState('30');
  const [meetingModalDesc, setMeetingModalDesc] = useState('');
  const [isSchedulingMeeting, setIsSchedulingMeeting] = useState(false);

  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const [isComposerPlusMenuOpen, setIsComposerPlusMenuOpen] = useState(false);
  const composerPlusMenuRef = useRef<HTMLDivElement>(null);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<any>(null);

  // User-adjustable composer height (persisted in localStorage, default 84px)
  const [composerHeight, setComposerHeight] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('pulse_chat_composer_height');
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed) && parsed >= 36 && parsed <= 420) return parsed;
      }
    } catch {
      // Ignore storage error
    }
    return 84;
  });

  const startComposerResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = textareaRef.current?.offsetHeight || composerHeight;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      // Dragging up (negative deltaY) increases height; dragging down decreases height
      const deltaY = startY - moveEvent.clientY;
      const nextHeight = Math.min(420, Math.max(36, startHeight + deltaY));
      setComposerHeight(nextHeight);
      try {
        localStorage.setItem('pulse_chat_composer_height', String(nextHeight));
      } catch {
        // Ignore storage error
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Pinned message navigation & highlight
  const [activePinnedIndex, setActivePinnedIndex] = useState<number>(0);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);

  const scrollToMessage = (messageId: string) => {
    const el = document.getElementById(`chat-msg-${messageId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedMessageId(messageId);
      setTimeout(() => {
        setHighlightedMessageId((current) => (current === messageId ? null : current));
      }, 2500);
    }
  };

  // Lightbox for full-screen WhatsApp image preview
  const [activeLightbox, setActiveLightbox] = useState<{
    url: string;
    downloadUrl?: string;
    fileName: string;
    senderName?: string;
    createdAt?: string;
  } | null>(null);

  // Helper to determine if an attachment or file is an image
  const isImageFile = (fileName?: string, mimeType?: string): boolean => {
    if (mimeType && mimeType.startsWith('image/')) return true;
    if (!fileName) return false;
    return /\.(jpg|jpeg|png|gif|webp|svg|bmp|heic|heif|avif)$/i.test(fileName);
  };

  // Get secure inline preview URL for an attachment
  const getAttachmentPreviewUrl = (att: ChatAttachment): string => {
    if (att.fileId) {
      return `/api/files/${att.fileId}/preview${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    }
    if (att.url) {
      const previewBase = att.url.includes('/download') ? att.url.replace('/download', '/preview') : att.url;
      return `${previewBase}${token ? (previewBase.includes('?') ? `&token=${encodeURIComponent(token)}` : `?token=${encodeURIComponent(token)}`) : ''}`;
    }
    return '';
  };

  // Get secure download / fallback URL for an attachment
  const getAttachmentDownloadUrl = (att: ChatAttachment): string => {
    const rawUrl = att.url || (att.fileId ? `/api/files/${att.fileId}/download` : '');
    if (!rawUrl) return '';
    if (token && !rawUrl.includes('token=')) {
      return `${rawUrl}${rawUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
    }
    return rawUrl;
  };

  // WhatsApp-style date separator
  const formatDateSeparator = (dateStr?: string | null): string => {
    if (!dateStr) return '';
    try {
      const sanitized = dateStr.includes(' ') && !dateStr.includes('T') ? dateStr.replace(' ', 'T') : dateStr;
      const d = new Date(sanitized);
      const now = new Date();
      if (d.toDateString() === now.toDateString()) return 'Today';
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
      return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Until the first load finishes the list shows a loading state, not "no conversations"
  const [channelsLoaded, setChannelsLoaded] = useState(false);

  // Fetch Channels
  const fetchChannels = async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/chat/channels', {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        const chs: ChatChannel[] = data.channels || [];
        setChannels(chs);
        // Open a chat by default only the first time; later refreshes must not reopen a chat
        // the user closed.
        if (!hasAutoOpenedChatRef.current && !selectedChannelId && chs.length > 0) {
          hasAutoOpenedChatRef.current = true;
          const firstActive = chs.find((c) => !c.isArchived) || chs[0];
          setSelectedChannelId(firstActive.id);
        }
      }
    } catch (err) {
      console.error('Failed to load channels:', err);
    } finally {
      setChannelsLoaded(true);
    }
  };

  // Fetch Team Members
  const fetchTeamMembers = async () => {
    setIsLoadingTeamMembers(true);
    try {
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (user?.id) headers['x-demo-user-id'] = user.id;

      let res = await fetch('/api/team/members', { headers });
      if (!res.ok) {
        res = await fetch('/api/team', { headers });
      }
      if (res.ok) {
        const data = await res.json();
        const rawList = Array.isArray(data) ? data : (data.members || data.users || []);
        const cleanMembers = rawList.map((m: any) => ({
          ...m,
          id: m.id,
          fullName: m.fullName || m.full_name || m.name || 'Team Member',
          email: m.email || '',
          avatarUrl: m.avatarUrl || m.avatar_url || undefined,
          role: m.role || 'member',
          title: m.title || '',
        }));
        setTeamMembers(cleanMembers);
      }
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    } finally {
      setIsLoadingTeamMembers(false);
    }
  };

  // Explicitly mark the active channel as read on the server
  const markChannelAsRead = async (channelId: string) => {
    if (!token || !channelId || document.visibilityState !== 'visible') return;
    try {
      await fetch(`/api/chat/channels/${channelId}/read`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
    } catch {
      // Ignore network error
    }
  };

  // Fetch Messages for Selected Channel
  const fetchMessages = async (channelId: string) => {
    if (!token) return;
    const cached = messageCacheRef.current.get(channelId);
    if (cached) {
      setMessages(cached);
      setIsLoadingMessages(false);
      scrollToBottom(true);
    } else {
      setIsLoadingMessages(true);
    }

    const shouldMarkRead = document.visibilityState === 'visible';

    try {
      const res = await fetch(
        `/api/chat/channels/${channelId}/messages?limit=60&markRead=${shouldMarkRead ? 'true' : 'false'}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        const msgList = data.messages || [];
        setMessages(msgList);
        messageCacheRef.current.set(channelId, msgList);
        setHasMoreMessages(msgList.length >= 60);
        // Force instant scroll to bottom so conversation opens with last message
        scrollToBottom(true);
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  // Load older messages when scrolled up
  const loadOlderMessages = async () => {
    if (!token || !selectedChannelId || isLoadingOlderMessages || !hasMoreMessages || messages.length === 0) return;
    const container = messagesContainerRef.current;
    if (!container) return;

    const previousScrollHeight = container.scrollHeight;
    const oldestTimestamp = messages[0].createdAt;
    setIsLoadingOlderMessages(true);

    try {
      const res = await fetch(`/api/chat/channels/${selectedChannelId}/messages?limit=40&markRead=false&before=${encodeURIComponent(oldestTimestamp)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        const older = data.messages || [];
        if (older.length === 0) {
          setHasMoreMessages(false);
        } else {
          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => m.id));
            const freshOlder = older.filter((m: any) => !existingIds.has(m.id));
            return [...freshOlder, ...prev];
          });
          requestAnimationFrame(() => {
            if (container) {
              container.scrollTop = container.scrollHeight - previousScrollHeight;
            }
          });
          if (older.length < 40) {
            setHasMoreMessages(false);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load older messages:', err);
    } finally {
      setIsLoadingOlderMessages(false);
    }
  };

  useEffect(() => {
    fetchChannels();
    fetchTeamMembers();
  }, [token, user?.id]);

  // Mark active channel as read when the user returns to the browser tab or window
  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible' && selectedChannelId) {
        markChannelAsRead(selectedChannelId);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
    };
  }, [selectedChannelId, token, user?.id]);

  // Refetch team members when opening New Channel modal
  useEffect(() => {
    if (isNewChannelModalOpen) {
      fetchTeamMembers();
      setNewGroupMemberSearch('');
      setIsQuickInviteOpen(false);
      setQuickInviteName('');
      setQuickInviteEmail('');
      setQuickInviteError(null);
    }
  }, [isNewChannelModalOpen]);

  // Refetch team members when opening Add Members modal
  useEffect(() => {
    if (isAddMembersModalOpen) {
      fetchTeamMembers();
      setAddMemberSearchQuery('');
      setSelectedNewMembers([]);
      setIsQuickInviteOpen(false);
      setQuickInviteName('');
      setQuickInviteEmail('');
      setQuickInviteError(null);
    }
  }, [isAddMembersModalOpen]);

  useEffect(() => {
    if (initialChannelId) {
      setSelectedChannelId(initialChannelId);
    }
  }, [initialChannelId]);

  useEffect(() => {
    if (selectedChannelId) {
      if (!messageCacheRef.current.has(selectedChannelId)) {
        setMessages([]);
      }
      initialScrollDoneRef.current[selectedChannelId] = false;
      setScrolledChannelId(null);
      joinChannel(selectedChannelId);
      fetchMessages(selectedChannelId);
      setReplyingTo(null);
      setEditingMessage(null);
      setIsMessageSearchOpen(false);
      setMessageSearchQuery('');
      setUnreadWhileScrolledUp(0);
      setActiveHeaderTab('chat');
      setIsProfileDrawerOpen(false);
      setProfileDrawerUser(null);
      setIsSlashMenuOpen(false);
      setSlashQuery('');
      setScheduledSendTime(null);
      setActivePinnedIndex(0);
      setHighlightedMessageId(null);
      // Mark channel as read locally
      setChannels((prev) =>
        prev.map((c) => (c.id === selectedChannelId ? { ...c, unreadCount: 0 } : c))
      );
    }
    return () => {
      if (selectedChannelId) {
        leaveChannel(selectedChannelId);
      }
    };
  }, [selectedChannelId]);

  useLayoutEffect(() => {
    // Only once the list holds this chat's own messages; right after switching chats it still
    // holds the previous chat's, and scrolling then would leave the new chat at its first message.
    if (selectedChannelId && messages.length > 0 && messages[messages.length - 1].channelId === selectedChannelId) {
      if (!initialScrollDoneRef.current[selectedChannelId]) {
        initialScrollDoneRef.current[selectedChannelId] = true;
        scrollToBottom(true);
        // The feed stays hidden while the late corrections below settle, then fades in already
        // at the bottom (not cleared on cleanup, or a message arriving meanwhile would keep it hidden).
        const channelId = selectedChannelId;
        setTimeout(() => setScrolledChannelId(channelId), 160);
        const t1 = setTimeout(() => scrollToBottom(true), 40);
        const t2 = setTimeout(() => scrollToBottom(true), 120);
        return () => {
          clearTimeout(t1);
          clearTimeout(t2);
        };
      }
    }
  }, [selectedChannelId, messages]);

  // Fetch Tasks for the Current Channel or Counterpart
  const fetchChannelTasks = async () => {
    if (!token || !selectedChannelId) return;
    setIsLoadingChannelTasks(true);
    try {
      const cur = channels.find((c) => c.id === selectedChannelId);
      const otherUser =
        cur?.type === 'direct'
          ? (cur.members || []).find((m) => m?.id !== user?.id)
          : null;
      let url = '/api/tasks';
      if (cur?.projectId) {
        url += `?projectId=${cur.projectId}`;
      } else if (otherUser) {
        url += `?assigneeId=${otherUser.id}`;
      }
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        let list = data.tasks || [];
        if (cur && cur.type !== 'direct' && !cur.projectId && Array.isArray(cur.members) && cur.members.length > 0) {
          const groupMemberIds = new Set(cur.members.map((m) => m.id));
          list = list.filter(
            (t: any) =>
              (t.assigneeId && groupMemberIds.has(t.assigneeId)) ||
              (Array.isArray(t.assignees) && t.assignees.some((a: any) => groupMemberIds.has(a.id)))
          );
        }
        setChannelTasks(list);
      }
    } catch (err) {
      console.error('Failed to fetch channel tasks:', err);
    } finally {
      setIsLoadingChannelTasks(false);
    }
  };

  // Fetch Meetings for Current Channel or Counterpart
  const fetchChannelMeetings = async () => {
    if (!token) return;
    setIsLoadingChannelMeetings(true);
    try {
      const res = await fetch('/api/meetings', {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        const cur = channels.find((c) => c.id === selectedChannelId);
        const otherUser =
          cur?.type === 'direct'
            ? (cur.members || []).find((m) => m?.id !== user?.id)
            : null;
        let list = data.meetings || [];
        if (otherUser) {
          list = list.filter((m: any) =>
            Array.isArray(m.attendees) &&
            m.attendees.some((a: any) => a.id === otherUser.id || a.id === user?.id)
          );
        }
        setChannelMeetings(list);
      }
    } catch (err) {
      console.error('Failed to fetch channel meetings:', err);
    } finally {
      setIsLoadingChannelMeetings(false);
    }
  };

  useEffect(() => {
    if (activeHeaderTab === 'tasks' && selectedChannelId) {
      fetchChannelTasks();
    } else if (activeHeaderTab === 'calendar' && selectedChannelId) {
      fetchChannelMeetings();
    }
  }, [activeHeaderTab, selectedChannelId]);

  // ClickUp Slash Commands
  const SLASH_COMMANDS = [
    { cmd: '/task', title: 'Create Task', icon: CheckSquare, desc: 'Create a new task in this channel' },
    { cmd: '/syncup', title: 'Start SyncUp', icon: PhoneCall, desc: 'Jump on a direct voice or video call' },
    { cmd: '/call', title: 'Audio / Video Call', icon: Video, desc: 'Start an audio or video call' },
    { cmd: '/meet', title: 'Schedule Meeting', icon: Calendar, desc: 'Book a 1-on-1 meeting or coffee sync' },
    { cmd: '/table', title: 'Insert Table', icon: Layers, desc: 'Insert markdown table template' },
    { cmd: '/poll', title: 'Create Poll', icon: Sparkles, desc: 'Start a team vote or question poll' },
    { cmd: '/remind', title: 'Set Reminder', icon: Clock, desc: 'Schedule a message or reminder' },
    { cmd: '/shrug', title: 'Shrug ¯\\_(ツ)_/¯', icon: Smile, desc: 'Insert the classic ASCII shrug emoji' },
  ];

  const filteredSlashCommands = SLASH_COMMANDS.filter((c) =>
    c.cmd.toLowerCase().includes(slashQuery.toLowerCase()) ||
    c.title.toLowerCase().includes(slashQuery.toLowerCase())
  );

  const executeSlashCommand = (cmd: string) => {
    setIsSlashMenuOpen(false);
    setSlashQuery('');
    if (cmd === '/task') {
      handleCreateTaskFromChat();
    } else if (cmd === '/syncup' || cmd === '/call') {
      const cur = channels.find((c) => c.id === selectedChannelId);
      const startWithVideo = cmd === '/call';
      if (cur?.type === 'direct') {
        const other =
          (cur.members || []).find((m) => m?.id !== user?.id) ||
          (teamMembers || []).find((m) => m?.id !== user?.id);
        if (other) {
          startDirectCall(
            {
              id: other.id,
              name: other.fullName || cur.name,
              avatarUrl: other.avatarUrl || undefined,
              role: other.title || other.role,
            },
            startWithVideo,
            cur.id
          );
        }
      } else if (cur) {
        startGroupCall({ id: cur.id, name: cur.name }, startWithVideo);
      }
      setInputContent('');
    } else if (cmd === '/meet') {
      setInputContent('');
      const cur = channels.find((c) => c.id === selectedChannelId);
      const other =
        cur?.type === 'direct'
          ? (cur.members || []).find((m) => m?.id !== user?.id)
          : null;
      setMeetingModalTitle(`SyncUp with ${other ? other.fullName : cur?.name || 'Team'}`);
      setMeetingModalDate(new Date().toISOString().split('T')[0]);
      setIsScheduleMeetingModalOpen(true);
    } else if (cmd === '/shrug') {
      setInputContent((prev) => prev.replace(/^\/\S*/, '¯\\_(ツ)_/¯ '));
      textareaRef.current?.focus();
    } else if (cmd === '/table') {
      setInputContent(
        '| Item | Status | Assignee |\n| --- | --- | --- |\n| Task 1 | In Progress | Team |\n| Task 2 | Completed | Team |'
      );
      textareaRef.current?.focus();
    } else if (cmd === '/poll') {
      setInputContent(
        '📊 **Poll: Which direction should we take?**\n1. Option A\n2. Option B\n3. Option C\n\n(React below with 1️⃣, 2️⃣, or 3️⃣)'
      );
      textareaRef.current?.focus();
    } else if (cmd === '/remind') {
      setIsScheduleMenuOpen(true);
    }
  };

  const handleOpenProfileDrawer = (targetUser: User | string) => {
    let userObj: User | null = null;
    if (typeof targetUser === 'string') {
      userObj = (teamMembers || []).find((m) => m.id === targetUser) || null;
    } else {
      userObj = targetUser;
    }
    if (userObj) {
      setProfileDrawerUser(userObj);
      setIsProfileDrawerOpen(true);
    } else {
      openUserProfile(targetUser);
    }
  };

  const handleScheduleMeetingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!meetingModalTitle || !meetingModalDate || !token || !selectedChannelId) return;

    setIsSchedulingMeeting(true);
    try {
      const cur = channels.find((c) => c.id === selectedChannelId);
      const otherUser =
        cur?.type === 'direct'
          ? (cur.members || []).find((m) => m?.id !== user?.id)
          : null;

      const attendees = [
        {
          id: user?.id || '',
          name: user?.fullName || 'Host',
          role: user?.role === 'admin' ? 'Administrator' : (user?.title || 'Member'),
          avatarUrl: user?.avatarUrl || undefined,
        },
      ];
      if (otherUser) {
        attendees.push({
          id: otherUser.id,
          name: otherUser.fullName,
          role: otherUser.role === 'admin' ? 'Administrator' : (otherUser.title || 'Member'),
          avatarUrl: otherUser.avatarUrl || undefined,
        });
      }

      const scheduledAtIso = `${meetingModalDate}T${meetingModalTime}:00`;

      const res = await fetch('/api/meetings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          title: meetingModalTitle,
          description: meetingModalDesc,
          scheduledAt: scheduledAtIso,
          durationMinutes: Number(meetingModalDuration),
          attendees,
        }),
      });

      if (res.ok) {
        const meetingDateFormatted = new Date(scheduledAtIso).toLocaleString([], {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });

        // Post announcement to chat
        const meetingMessage = `📅 **Meeting Scheduled: ${meetingModalTitle}**\n🕒 Time: ${meetingDateFormatted} (${meetingModalDuration} mins)\n👥 Attendees: ${attendees.map((a) => a.name).join(', ')}${meetingModalDesc ? `\n📝 Agenda: ${meetingModalDesc}` : ''}\n🔗 Open in Calendar tab`;

        await fetch(`/api/chat/channels/${selectedChannelId}/messages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
          body: JSON.stringify({ content: meetingMessage }),
        });

        setIsScheduleMeetingModalOpen(false);
        setMeetingModalTitle('');
        setMeetingModalDate('');
        setMeetingModalDesc('');
        fetchChannelMeetings();
        fetchMessages(selectedChannelId);
      }
    } catch (err) {
      console.error('Failed to schedule meeting:', err);
    } finally {
      setIsSchedulingMeeting(false);
    }
  };

  // Real-time Event Subscription (WhatsApp synchronization across all channels & tabs)
  useEffect(() => {
    const unsubscribe = subscribe((event) => {
      if (event.type === 'chat_message') {
        const newMsg = event.data;
        if (newMsg.channelId === selectedChannelId) {
          setMessages((prev) => {
            if (prev.find((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });

          // Update channel snippet in sidebar
          setChannels((prev) =>
            prev.map((c) =>
              c.id === newMsg.channelId
                ? {
                    ...c,
                    lastMessage: {
                      content: newMsg.content,
                      senderName: newMsg.senderName,
                      createdAt: newMsg.createdAt,
                    },
                  }
                : c
            )
          );

          // If scrolled up, increment unread counter badge; otherwise scroll down
          if (showScrollBottom) {
            setUnreadWhileScrolledUp((prev) => prev + 1);
          } else {
            scrollToBottom();
          }

          if (newMsg.senderId !== user?.id) {
            const currentChan = channels.find((c) => c.id === selectedChannelId);
            if (!currentChan?.isMuted) {
              playIncomingMessageSound();
            }
            // Only mark as read if the user's browser tab is visible!
            if (document.visibilityState === 'visible') {
              markChannelAsRead(selectedChannelId);
            }
          }
        } else if (String(newMsg.channelId || '').startsWith('chan-meeting-')) {
          // in-call chat of a meeting: not part of Messages
        } else {
          // Message in another conversation: if channel isn't in sidebar yet (e.g. newly created group), fetch channels
          setChannels((prev) => {
            const exists = prev.some((c) => c.id === newMsg.channelId);
            if (!exists) {
              fetchChannels();
              return prev;
            }
            return prev.map((c) => {
              if (c.id === newMsg.channelId) {
                return {
                  ...c,
                  lastMessage: {
                    content: newMsg.content,
                    senderName: newMsg.senderName,
                    createdAt: newMsg.createdAt,
                  },
                  unreadCount: newMsg.senderId !== user?.id ? (c.unreadCount || 0) + 1 : c.unreadCount,
                };
              }
              return c;
            });
          });

          const targetChan = channels.find((c) => c.id === newMsg.channelId);
          if (newMsg.senderId !== user?.id && !targetChan?.isMuted) {
            playIncomingMessageSound();
          }
        }
      } else if (event.type === 'channel_created') {
        fetchChannels();
      } else if (event.type === 'message_edited') {
        const { messageId, channelId, content, isEdited, updatedAt } = event.data;
        if (channelId === selectedChannelId) {
          setMessages((prev) =>
            prev.map((m) => (m.id === messageId ? { ...m, content, isEdited, updatedAt } : m))
          );
        }
        // the chat list preview may be this message
        fetchChannels();
      } else if (event.type === 'message_deleted') {
        const { messageId, channelId, deletedForEveryone, deletedForUserId } = event.data;
        if (channelId === selectedChannelId) {
          if (deletedForEveryone) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === messageId
                  ? {
                      ...m,
                      isDeleted: true,
                      content: '🚫 This message was deleted',
                      attachments: [],
                      reactions: [],
                    }
                  : m
              )
            );
          } else if (deletedForUserId === user?.id) {
            setMessages((prev) => prev.filter((m) => m.id !== messageId));
          }
        }
        // the chat list preview may have been this message
        fetchChannels();
      } else if (event.type === 'message_reaction') {
        const { messageId, channelId, reactions } = event.data;
        if (channelId === selectedChannelId) {
          setMessages((prev) =>
            prev.map((m) => (m.id === messageId ? { ...m, reactions } : m))
          );
        }
      } else if (event.type === 'message_pinned') {
        const { messageId, channelId, isPinned } = event.data;
        if (channelId === selectedChannelId) {
          setMessages((prev) =>
            prev.map((m) => (m.id === messageId ? { ...m, isPinned } : m))
          );
        }
      } else if (event.type === 'channel_read') {
        const { channelId, userId: readerId, userName: readerName, avatarUrl: readerAvatar, readAt } = event.data as any;
        // Record this member as a reader of my messages. A message only turns "seen" (blue ticks)
        // once every other member of the chat has read it, not as soon as one of them has.
        if (channelId === selectedChannelId && readerId && readerId !== user?.id) {
          const readTime = readAt || new Date().toISOString();
          const memberCount = channelsRef.current.find((c) => c.id === channelId)?.members?.length || 2;
          setMessages((prev) =>
            prev.map((m) => {
              if (m.senderId !== user?.id) return m;
              // Only messages that already existed when they read the chat
              if (new Date(m.createdAt).getTime() > new Date(readTime).getTime()) return m;
              if ((m.readBy || []).some((r) => r.userId === readerId)) return m;
              const readBy = [
                ...(m.readBy || []),
                { userId: readerId, userName: readerName || 'Member', avatarUrl: readerAvatar || null, readAt: readTime },
              ];
              const recipients = m.recipientCount ?? Math.max(1, memberCount - 1);
              return { ...m, readBy, isSeen: readBy.length >= recipients };
            })
          );
        }
        if (readerId === user?.id) {
          setChannels((prev) =>
            prev.map((c) => (c.id === channelId ? { ...c, unreadCount: 0 } : c))
          );
        }
      } else if (event.type === 'channel_unread') {
        if (event.data.userId === user?.id) {
          setChannels((prev) =>
            prev.map((c) =>
              c.id === event.data.channelId
                ? { ...c, unreadCount: Math.max(1, (c.unreadCount || 0) + 1) }
                : c
            )
          );
        }
      } else if (event.type === 'channel_pinned') {
        if (event.data.userId === user?.id) {
          setChannels((prev) =>
            prev.map((c) =>
              c.id === event.data.channelId ? { ...c, isPinned: event.data.isPinned } : c
            )
          );
        }
      } else if (event.type === 'channel_muted') {
        if (event.data.userId === user?.id) {
          setChannels((prev) =>
            prev.map((c) =>
              c.id === event.data.channelId ? { ...c, isMuted: event.data.isMuted } : c
            )
          );
        }
      } else if (event.type === 'channel_archived') {
        if (event.data.userId === user?.id) {
          setChannels((prev) =>
            prev.map((c) =>
              c.id === event.data.channelId ? { ...c, isArchived: event.data.isArchived } : c
            )
          );
        }
      } else if (event.type === 'typing_indicator') {
        const { channelId, userId: typerId, userName, isTyping } = event.data;
        if (channelId === selectedChannelId && typerId !== user?.id) {
          setTypingUsers((prev) => {
            const next = { ...prev };
            if (isTyping) next[typerId] = userName;
            else delete next[typerId];
            return next;
          });
        }
      } else if (event.type === 'channel_members_updated') {
        const { channelId, members } = event.data;
        setChannels((prev) => {
          const exists = prev.some((c) => c.id === channelId);
          if (!exists) {
            fetchChannels();
            return prev;
          }
          return prev.map((c) => (c.id === channelId ? { ...c, members } : c));
        });
      } else if (
        event.type === 'task_created' ||
        event.type === 'task_updated' ||
        event.type === 'task_archived' ||
        event.type === 'task_restored' ||
        event.type === 'tasks_reordered'
      ) {
        fetchChannelTasks();
      } else if (
        event.type === 'meeting_created' ||
        event.type === 'meeting_updated' ||
        event.type === 'meeting_deleted'
      ) {
        fetchChannelMeetings();
      }
    });

    const handleLocalTaskMutation = () => {
      fetchChannelTasks();
    };
    window.addEventListener('task-created', handleLocalTaskMutation);
    window.addEventListener('task-updated', handleLocalTaskMutation);
    window.addEventListener('task-archived', handleLocalTaskMutation);
    window.addEventListener('task-restored', handleLocalTaskMutation);

    return () => {
      unsubscribe();
      window.removeEventListener('task-created', handleLocalTaskMutation);
      window.removeEventListener('task-updated', handleLocalTaskMutation);
      window.removeEventListener('task-archived', handleLocalTaskMutation);
      window.removeEventListener('task-restored', handleLocalTaskMutation);
    };
  }, [subscribe, selectedChannelId, user?.id, showScrollBottom]);

  const scrollToBottom = (instant = false) => {
    const doScroll = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
      messagesEndRef.current?.scrollIntoView({ behavior: instant ? 'auto' : 'smooth' });
      setShowScrollBottom(false);
      setUnreadWhileScrolledUp(0);
    };

    if (instant) {
      doScroll();
      requestAnimationFrame(doScroll);
      setTimeout(doScroll, 50);
      setTimeout(doScroll, 150);
    } else {
      setTimeout(doScroll, 50);
    }
  };

  // Scroll listener for floating scroll-to-bottom button and scroll-up pagination
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const isUp = container.scrollHeight - container.scrollTop - container.clientHeight > 280;
    setShowScrollBottom(isUp);
    if (!isUp) {
      setUnreadWhileScrolledUp(0);
    }

    // Seamless scroll-up pagination
    if (container.scrollTop < 40 && hasMoreMessages && !isLoadingOlderMessages && messages.length > 0 && selectedChannelId) {
      loadOlderMessages();
    }
  };

  // Close active channel, modals, or drawers on Escape key
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        if (isNewChannelModalOpen) {
          setIsNewChannelModalOpen(false);
        } else if (isProfileDrawerOpen) {
          setIsProfileDrawerOpen(false);
        } else if (isMembersDrawerOpen) {
          setIsMembersDrawerOpen(false);
        } else if (isMessageSearchOpen) {
          setIsMessageSearchOpen(false);
        } else if (isComposerPlusMenuOpen) {
          setIsComposerPlusMenuOpen(false);
        } else if (selectedChannelId) {
          setSelectedChannelId(null);
        }
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isNewChannelModalOpen, isProfileDrawerOpen, isMembersDrawerOpen, isMessageSearchOpen, isComposerPlusMenuOpen, selectedChannelId]);

  // Upload attachments from desktop or drag-and-drop
  const uploadChatFiles = async (incoming: FileList | File[]) => {
    if (!incoming || incoming.length === 0 || !selectedChannelId) return;
    const files = filterUploadableFiles(incoming);
    if (files.length === 0) return;

    const activeToken = token || localStorage.getItem('nexus_auth_token') || '';

    setIsUploadingAttachment(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        if (selectedChannelId) {
          formData.append('channelId', selectedChannelId);
        }

        const res = await fetch('/api/files/upload', {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...(activeToken ? { Authorization: `Bearer ${activeToken}` } : {}),
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
          body: formData,
        });

        if (res.ok) {
          const data = await res.json();
          const rec = data.file;
          const isImg = isImageFile(rec.originalName, rec.mimeType);
          setAttachedFiles((prev) => [
            ...prev,
            {
              fileId: rec.id,
              fileName: rec.originalName,
              fileSize: rec.fileSize,
              mimeType: rec.mimeType,
              url: isImg
                ? rec.previewUrl || `/api/files/${rec.id}/preview`
                : rec.downloadUrl || `/api/files/${rec.id}/download`,
            },
          ]);
        } else {
          // The reply may not be JSON (e.g. an HTML error page from a proxy), so read it as text first
          const errText = await res.text().catch(() => '');
          let serverMessage = '';
          try {
            serverMessage = JSON.parse(errText).error || '';
          } catch {
            // not JSON — fall back to the HTTP status below
          }
          console.error('Failed to upload file attachment:', res.status, errText.slice(0, 500));
          alert(
            serverMessage ||
              `Could not upload "${file.name}" (server responded ${res.status}${res.statusText ? ` ${res.statusText}` : ''}).`
          );
        }
      }
    } catch (err: any) {
      console.error('Failed to upload file attachment:', err);
      alert(err.message || 'An error occurred while uploading file attachment.');
    } finally {
      setIsUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      uploadChatFiles(e.target.files);
    }
  };

  const handleChatDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOverChat(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      uploadChatFiles(e.dataTransfer.files);
    }
  };

  // Extract and normalize screenshots or files from clipboard paste event
  const extractClipboardFiles = (clipboardData?: DataTransfer | null): File[] => {
    if (!clipboardData) return [];
    const rawFiles: File[] = [];

    if (clipboardData.items) {
      for (let i = 0; i < clipboardData.items.length; i++) {
        const item = clipboardData.items[i];
        if (item.kind === 'file') {
          const f = item.getAsFile();
          if (f) rawFiles.push(f);
        }
      }
    }

    if (rawFiles.length === 0 && clipboardData.files && clipboardData.files.length > 0) {
      for (let i = 0; i < clipboardData.files.length; i++) {
        rawFiles.push(clipboardData.files[i]);
      }
    }

    if (rawFiles.length === 0) return [];

    const now = new Date();
    const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate()
    ).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(
      2,
      '0'
    )}${String(now.getSeconds()).padStart(2, '0')}`;

    return rawFiles.map((f, idx) => {
      const isGenericName =
        !f.name || /^image\.(png|jpe?g|webp|gif|bmp)$/i.test(f.name) || f.name === 'blob';
      if (isGenericName) {
        const mime = f.type || 'image/png';
        const ext =
          mime.split('/')[1]?.replace('jpeg', 'jpg')?.replace(/[^a-z0-9]/gi, '') || 'png';
        const suffix = rawFiles.length > 1 ? `-${idx + 1}` : '';
        return new File([f], `screenshot-${timestamp}${suffix}.${ext}`, { type: mime });
      }
      return f;
    });
  };

  const handleChatPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedFiles = extractClipboardFiles(e.clipboardData);
    if (pastedFiles.length > 0) {
      const plainText = e.clipboardData?.getData('text/plain');
      if (!plainText) {
        e.preventDefault();
      }
      uploadChatFiles(pastedFiles);
    }
  };

  // Allow pasting screenshots anywhere in the active chat view even when textarea isn't focused
  useEffect(() => {
    if (!selectedChannelId || activeHeaderTab !== 'chat') return;

    const handleGlobalPaste = (e: ClipboardEvent) => {
      const activeEl = document.activeElement as HTMLElement | null;
      // Skip if user is already focused in the composer textarea (handled by onPaste) or another input/textarea
      if (
        activeEl &&
        (activeEl === textareaRef.current ||
          activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.isContentEditable)
      ) {
        return;
      }

      const pastedFiles = extractClipboardFiles(e.clipboardData);
      if (pastedFiles.length > 0) {
        e.preventDefault();
        uploadChatFiles(pastedFiles);
        textareaRef.current?.focus();
      }
    };

    window.addEventListener('paste', handleGlobalPaste);
    return () => window.removeEventListener('paste', handleGlobalPaste);
  }, [selectedChannelId, activeHeaderTab, token]);

  const handleRemoveAttachment = (fileId: string) => {
    setAttachedFiles((prev) => prev.filter((a) => a.fileId !== fileId));
  };

  // Voice Note Recording (WhatsApp-grade)
  const startVoiceRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Voice recording is not supported in this browser.');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
      };

      recorder.start();
      setIsRecordingVoice(true);
      setRecordingSeconds(0);
      voiceTimerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Microphone access denied:', err);
      alert('Unable to access microphone. Please allow microphone permission.');
    }
  };

  const cancelVoiceRecording = () => {
    if (voiceTimerRef.current) clearInterval(voiceTimerRef.current);
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    audioChunksRef.current = [];
    setIsRecordingVoice(false);
    setRecordingSeconds(0);
  };

  const sendVoiceRecording = async () => {
    if (!mediaRecorderRef.current || !selectedChannelId || !token) return;
    if (voiceTimerRef.current) clearInterval(voiceTimerRef.current);
    const duration = recordingSeconds;
    const recorder = mediaRecorderRef.current;

    recorder.onstop = async () => {
      try {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        audioChunksRef.current = [];
        setIsRecordingVoice(false);
        setRecordingSeconds(0);

        if (audioBlob.size < 400) return;

        const formData = new FormData();
        const file = new File([audioBlob], `voice-note-${Date.now()}.webm`, { type: 'audio/webm' });
        formData.append('file', file);
        formData.append('channelId', selectedChannelId);

        const uploadRes = await fetch('/api/files/upload', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
          body: formData,
        });

        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          const rec = uploadData.file;
          const previewUrl = `/api/files/${rec.id}/preview${token ? `?token=${encodeURIComponent(token)}` : ''}`;

          const msgRes = await fetch(`/api/chat/channels/${selectedChannelId}/messages`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
            },
            body: JSON.stringify({
              content: '🎤 Voice message',
              voiceNote: {
                durationSeconds: Math.max(1, duration),
                audioUrl: previewUrl,
              },
            }),
          });

          if (msgRes.ok) {
            const data = await msgRes.json();
            setMessages((prev) => {
              if (prev.find((m) => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
            playOutgoingMessageSound();
            scrollToBottom();
          }
        }
      } catch (err) {
        console.error('Failed to send voice note:', err);
      }
    };

    recorder.stop();
  };

  // Current active channel
  const currentChannel = (channels || []).find((c) => c.id === selectedChannelId);

  // Mentions Autocomplete: Strictly scoped to current channel's participants/members
  const mentionCandidates = useMemo(() => {
    if (currentChannel && Array.isArray(currentChannel.members) && currentChannel.members.length > 0) {
      return currentChannel.members;
    }
    return teamMembers || [];
  }, [currentChannel?.members, teamMembers]);

  const filteredMentionMembers =
    mentionQuery !== null
      ? mentionCandidates
          .filter((m) => {
            if (!m) return false;
            const q = mentionQuery.toLowerCase();
            const nameMatch = m.fullName ? m.fullName.toLowerCase().includes(q) : false;
            const emailMatch = m.email ? m.email.toLowerCase().includes(q) : false;
            return nameMatch || emailMatch;
          })
          .slice(0, 10)
      : [];

  const insertMention = (member: User) => {
    if (mentionMatchIndex === -1) return;
    const cursorPos = textareaRef.current?.selectionStart || inputContent.length;
    const beforeMention = inputContent.slice(0, mentionMatchIndex);
    const afterCursor = inputContent.slice(cursorPos);
    const mentionText = `@${member.fullName} `;
    const newContent = `${beforeMention}${mentionText}${afterCursor}`;
    setInputContent(newContent);
    setMentionQuery(null);
    setMentionMatchIndex(-1);
    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = beforeMention.length + mentionText.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 10);
  };

  // Latest chat list for realtime handlers (their closures can be stale)
  const channelsRef = useRef<ChatChannel[]>([]);
  channelsRef.current = channels;

  // Send Message
  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if ((!inputContent.trim() && attachedFiles.length === 0) || !selectedChannelId) return;

    // Check if editing an existing message
    if (editingMessage) {
      try {
        const res = await fetch(`/api/chat/messages/${editingMessage.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token || ''}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
          body: JSON.stringify({ content: inputContent.trim() }),
        });
        if (res.ok) {
          const data = await res.json();
          setMessages((prev) =>
            prev.map((m) => (m.id === data.message.id ? data.message : m))
          );
          setEditingMessage(null);
          setInputContent('');
        }
      } catch (err) {
        console.error('Failed to edit message:', err);
      }
      return;
    }

    const scheduledNote = scheduledSendTime ? `[⏰ Scheduled: ${scheduledSendTime}]\n` : '';
    const isSingleImage =
      attachedFiles.length === 1 &&
      isImageFile(attachedFiles[0].fileName, attachedFiles[0].mimeType);
    const content =
      (scheduledNote + inputContent.trim()) ||
      (attachedFiles.length > 0
        ? isSingleImage
          ? 'Photo'
          : `Shared ${attachedFiles.length} file(s)`
        : '');
    const replyToId = replyingTo ? replyingTo.id : null;
    const currentAttachments = [...attachedFiles];

    setInputContent('');
    setAttachedFiles([]);
    setReplyingTo(null);
    setScheduledSendTime(null);
    sendTyping(selectedChannelId, false);

    try {
      const res = await fetch(`/api/chat/channels/${selectedChannelId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          content,
          replyToId,
          attachments: currentAttachments,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setMessages((prev) => {
          if (prev.find((m) => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
        playOutgoingMessageSound();
        scrollToBottom();
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  // Delete Message ("Delete for me" or "Delete for everyone")
  const handleDeleteMessage = async (msg: ChatMessage, forEveryone: boolean) => {
    try {
      const res = await fetch(`/api/chat/messages/${msg.id}?forEveryone=${forEveryone}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        if (forEveryone) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === msg.id
                ? {
                    ...m,
                    isDeleted: true,
                    content: '🚫 This message was deleted',
                    attachments: [],
                    reactions: [],
                  }
                : m
            )
          );
        } else {
          setMessages((prev) => prev.filter((m) => m.id !== msg.id));
        }
        setDeleteModalMessage(null);
        setActiveMobileMessageId(null);
        fetchChannels();
      }
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  };

  // Forward Message
  const handleForwardMessage = async (targetChannelId: string) => {
    if (!forwardingMessage) return;
    try {
      const res = await fetch(`/api/chat/messages/${forwardingMessage.id}/forward`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({ targetChannelId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `The server could not forward the message (HTTP ${res.status}).`);
      }
      const data = await res.json();
      if (data.channelId === selectedChannelId) {
        setMessages((prev) => (prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]));
        scrollToBottom();
      }
      setForwardingMessage(null);
    } catch (err) {
      console.error('Failed to forward message:', err);
      // The forward dialog stays open and shows the reason
      throw err;
    }
  };

  // Channel Actions: Pin, Mute, Archive, Unread
  const handleToggleChannelPin = async (channelId: string) => {
    try {
      const res = await fetch(`/api/chat/channels/${channelId}/pin`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setChannels((prev) =>
          prev.map((c) => (c.id === channelId ? { ...c, isPinned: data.isPinned } : c))
        );
        setActiveChannelMenuId(null);
      }
    } catch (err) {
      console.error('Failed to toggle pin on channel:', err);
    }
  };

  const handleToggleChannelMute = async (channelId: string) => {
    try {
      const res = await fetch(`/api/chat/channels/${channelId}/mute`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setChannels((prev) =>
          prev.map((c) => (c.id === channelId ? { ...c, isMuted: data.isMuted } : c))
        );
        setActiveChannelMenuId(null);
      }
    } catch (err) {
      console.error('Failed to toggle mute on channel:', err);
    }
  };

  const handleToggleChannelArchive = async (channelId: string) => {
    try {
      const res = await fetch(`/api/chat/channels/${channelId}/archive`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setChannels((prev) => {
          const updated = prev.map((c) =>
            c.id === channelId ? { ...c, isArchived: data.isArchived } : c
          );
          if (data.isArchived && selectedChannelId === channelId && channelFilter !== 'archived') {
            const nextActive = updated.find((c) => !c.isArchived && c.id !== channelId);
            setSelectedChannelId(nextActive ? nextActive.id : null);
          }
          return updated;
        });
        setActiveChannelMenuId(null);
      }
    } catch (err) {
      console.error('Failed to toggle archive on channel:', err);
    }
  };

  const handleMarkChannelUnread = async (channelId: string) => {
    try {
      const res = await fetch(`/api/chat/channels/${channelId}/unread`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        setChannels((prev) =>
          prev.map((c) => (c.id === channelId ? { ...c, unreadCount: Math.max(1, c.unreadCount || 1) } : c))
        );
        setActiveChannelMenuId(null);
      }
    } catch (err) {
      console.error('Failed to mark channel unread:', err);
    }
  };

  // Create Task from Chat (IHB Pulse Hallmark Feature)
  const handleCreateTaskFromChat = () => {
    const raw = inputContent.trim();
    const firstLine = raw ? raw.split('\n')[0].slice(0, 80) : '';
    setDraftTaskTitle(firstLine);
    setDraftTaskDescription(raw);
    setIsTaskModalOpen(true);
  };

  const handleCreateTaskFromMessage = (msg: ChatMessage) => {
    const firstLine = msg.content ? msg.content.split('\n')[0].slice(0, 80) : '';
    setDraftTaskTitle(firstLine);
    setDraftTaskDescription(msg.content);
    setIsTaskModalOpen(true);
    setActiveMobileMessageId(null);
  };

  const handleChatTaskCreated = async (createdTask?: any) => {
    setIsTaskModalOpen(false);
    setInputContent('');
    setAttachedFiles([]);

    if (!createdTask || !selectedChannelId) return;

    window.dispatchEvent(new CustomEvent('task-created', { detail: createdTask }));
    fetchChannelTasks();

    const origin = window.location.origin;
    const taskLink = `${origin}/tasks/${createdTask.id}`;
    const code = createdTask.code || 'TSK';
    const taskMessage = `📋 Created Task [${code}]: ${createdTask.title}
Status: ${(createdTask.status || 'todo').toUpperCase()} • Priority: ${(createdTask.priority || 'medium').toUpperCase()}${createdTask.projectName ? ` • Project: ${createdTask.projectName}` : ''}
Task Link: ${taskLink}`;

    try {
      const res = await fetch(`/api/chat/channels/${selectedChannelId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          content: taskMessage,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setMessages((prev) => {
          if (prev.find((m) => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
        scrollToBottom();
      }
    } catch (err) {
      console.error('Failed to post task message to chat:', err);
    }
  };

  // Keyboard shortcut listener: Enter to send, Shift+Enter for newline, Ctrl+Enter to create task
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isSlashMenuOpen && filteredSlashCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSlashIndex((prev) => (prev + 1) % filteredSlashCommands.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSlashIndex((prev) => (prev - 1 + filteredSlashCommands.length) % filteredSlashCommands.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        executeSlashCommand(filteredSlashCommands[selectedSlashIndex].cmd);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsSlashMenuOpen(false);
        return;
      }
    }

    if (mentionQuery !== null && filteredMentionMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex((prev) => (prev + 1) % filteredMentionMembers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex((prev) => (prev - 1 + filteredMentionMembers.length) % filteredMentionMembers.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMentionMembers[selectedMentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionQuery(null);
        return;
      }
    }

    if (e.key === 'Enter') {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        handleCreateTaskFromChat();
      } else if (e.shiftKey) {
        // Allow standard newline
      } else {
        e.preventDefault();
        handleSendMessage();
      }
    }
    if (e.key === 'Escape' && editingMessage) {
      e.preventDefault(); // cancels the edit only; the global Escape handler then leaves the chat open
      setEditingMessage(null);
      setInputContent('');
    }
  };

  const handleToggleReaction = async (messageId: string, emoji: string) => {
    try {
      await fetch(`/api/chat/messages/${messageId}/reactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({ emoji }),
      });
    } catch (err) {
      console.error('Failed to toggle reaction:', err);
    }
  };

  const handleTogglePin = async (messageId: string, explicitPinned?: boolean) => {
    // Optimistically update message pin status
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId) return m;
        const nextPinned = typeof explicitPinned === 'boolean' ? explicitPinned : !m.isPinned;
        return { ...m, isPinned: nextPinned };
      })
    );

    try {
      const res = await fetch(`/api/chat/messages/${messageId}/pin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: typeof explicitPinned === 'boolean' ? JSON.stringify({ isPinned: explicitPinned }) : undefined,
      });

      if (!res.ok) {
        // Rollback on server error
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== messageId) return m;
            const reverted = typeof explicitPinned === 'boolean' ? !explicitPinned : !m.isPinned;
            return { ...m, isPinned: reverted };
          })
        );
      }
    } catch (err) {
      console.error('Failed to toggle pin:', err);
      // Rollback on network error
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m;
          const reverted = typeof explicitPinned === 'boolean' ? !explicitPinned : !m.isPinned;
          return { ...m, isPinned: reverted };
        })
      );
    }
  };

  // Handle Quick Invite directly from Group Creation and Add Members modals
  const handleQuickInviteMember = async (targetModal: 'newChannel' | 'addMembers') => {
    if (!quickInviteEmail.trim()) {
      setQuickInviteError('Please enter an email address.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(quickInviteEmail.trim())) {
      setQuickInviteError('Please enter a valid email address (e.g. name@company.com).');
      return;
    }

    setIsSubmittingQuickInvite(true);
    setQuickInviteError(null);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (user?.id) headers['x-demo-user-id'] = user.id;

      const res = await fetch('/api/team/invite', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          emails: [quickInviteEmail.trim()],
          fullName: quickInviteName.trim() || undefined,
          role: 'member',
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to invite team member.');
      }

      // Check newly created or existing user info
      const invitedUser = data.invited && data.invited[0];
      const newUserId = invitedUser?.id;

      // Refresh team members list
      await fetchTeamMembers();

      if (targetModal === 'newChannel') {
        if (newUserId) {
          setNewGroupMemberIds((prev) => (prev.includes(newUserId) ? prev : [...prev, newUserId]));
        }
        setNewGroupMemberSearch('');
      } else {
        if (newUserId) {
          setSelectedNewMembers((prev) => (prev.includes(newUserId) ? prev : [...prev, newUserId]));
        }
        setAddMemberSearchQuery('');
      }

      setIsQuickInviteOpen(false);
      setQuickInviteName('');
      setQuickInviteEmail('');
    } catch (err: any) {
      console.error('Quick invite failed:', err);
      setQuickInviteError(err.message || 'An error occurred while inviting the colleague.');
    } finally {
      setIsSubmittingQuickInvite(false);
    }
  };

  const handleCreateChannel = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/chat/channels', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          type: newChannelType,
          name: newChannelName,
          targetUserId: newDirectTargetUserId,
          memberIds: newChannelType === 'group' ? newGroupMemberIds : undefined,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setIsNewChannelModalOpen(false);
        setNewChannelName('');
        setNewDirectTargetUserId('');
        setNewGroupMemberIds([]);
        setNewGroupMemberSearch('');
        await fetchChannels();
        if (data.channelId) {
          setSelectedChannelId(data.channelId);
        }
      }
    } catch (err) {
      console.error('Failed to create channel:', err);
    }
  };

  const handleAddMembersToChannel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedChannelId || selectedNewMembers.length === 0) return;

    setIsAddingMembers(true);
    try {
      const res = await fetch(`/api/chat/channels/${selectedChannelId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({ memberIds: selectedNewMembers }),
      });

      if (res.ok) {
        setIsAddMembersModalOpen(false);
        setSelectedNewMembers([]);
        setAddMemberSearchQuery('');
        await fetchChannels();
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(errData.error || 'Failed to add members to channel.');
      }
    } catch (err) {
      console.error('Failed to add members to channel:', err);
    } finally {
      setIsAddingMembers(false);
    }
  };

  const handleRemoveMemberFromChannel = async (targetUserId: string, targetUserName: string) => {
    if (!selectedChannelId) return;
    const isSelf = targetUserId === user?.id;
    const confirmText = isSelf
      ? 'Are you sure you want to leave this channel?'
      : `Are you sure you want to remove ${targetUserName} from this channel?`;
    if (!window.confirm(confirmText)) return;

    try {
      const res = await fetch(`/api/chat/channels/${selectedChannelId}/members/${targetUserId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        await fetchChannels();
        if (isSelf) {
          setIsMembersDrawerOpen(false);
        }
      }
    } catch (err) {
      console.error('Failed to remove member:', err);
    }
  };

  // Filtered Channels for Sidebar
  const archivedCount = (channels || []).filter((c) => Boolean(c.isArchived)).length;
  const filteredChannels = (channels || []).filter((c) => {
    if (channelFilter === 'archived') {
      if (!c.isArchived) return false;
    } else {
      if (c.isArchived) return false;
      if (channelFilter === 'unread' && (!c.unreadCount || c.unreadCount === 0)) return false;
      if (channelFilter === 'pinned' && !c.isPinned) return false;
      if (channelFilter === 'unpinned' && c.isPinned) return false;
      if (channelFilter === 'groups' && c.type === 'direct') return false;
      if (channelFilter === 'direct' && c.type !== 'direct') return false;
    }
    if (chatSearchQuery.trim()) {
      const q = chatSearchQuery.toLowerCase();
      const nameMatch = c.name.toLowerCase().includes(q);
      const memberMatch = (c.members || []).some((m) => m?.fullName?.toLowerCase().includes(q));
      const lastMsgMatch = c.lastMessage?.content?.toLowerCase().includes(q);
      if (!nameMatch && !memberMatch && !lastMsgMatch) return false;
    }
    return true;
  }).sort((a, b) => {
    // pinned chats first, then the chat with the newest message on top (like WhatsApp)
    const pin = Number(Boolean(b.isPinned)) - Number(Boolean(a.isPinned));
    if (pin !== 0) return pin;
    const t = (c: any) => Date.parse(c.lastMessage?.createdAt || c.createdAt || '') || 0;
    return t(b) - t(a);
  });

  // Filtered Messages if In-Chat Search is Active
  const visibleMessages = messages.filter((m) => {
    if (!messageSearchQuery.trim()) return true;
    const q = messageSearchQuery.toLowerCase();
    const contentMatch = m.content.toLowerCase().includes(q);
    const senderMatch = m.senderName.toLowerCase().includes(q);
    return contentMatch || senderMatch;
  });

  const pinnedMessages = messages.filter((m) => m.isPinned && !m.isDeleted);
  const safePinnedIndex = pinnedMessages.length > 0 ? Math.min(activePinnedIndex, pinnedMessages.length - 1) : 0;
  const currentPinnedMsg = pinnedMessages.length > 0 ? pinnedMessages[safePinnedIndex] : null;

  return (
    <div className="flex-1 min-h-0 h-[calc(100vh-4.25rem)] bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 shadow-card flex overflow-hidden">
      {/* ----------------- LEFT CHANNELS LIST SIDEBAR ----------------- */}
      <div
        className={`${
          selectedChannelId ? 'hidden md:flex' : 'flex'
        } w-full md:w-80 border-r border-surface-200 dark:border-surface-800 flex-col bg-[#f0f2f5]/40 dark:bg-surface-950/40 shrink-0`}
      >
        {/* Top Header */}
        <div className="p-3.5 border-b border-surface-200/80 dark:border-surface-800 flex items-center justify-between bg-white dark:bg-surface-900">
          <div className="flex items-center space-x-2">
            <MessageSquare className="w-5 h-5 text-brand-600 dark:text-brand-400 stroke-[2.2]" />
            <h2 className="font-bold text-sm text-surface-900 dark:text-surface-100 tracking-tight">
              {channelFilter === 'archived' ? 'Archived Chats' : 'Chats'}
            </h2>
          </div>
          <div className="flex items-center space-x-1">
            {/* One button for pinned / unpinned: click cycles Pinned -> Unpinned -> all chats */}
            <button
              type="button"
              onClick={() =>
                setChannelFilter(
                  channelFilter === 'pinned' ? 'unpinned' : channelFilter === 'unpinned' ? 'all' : 'pinned'
                )
              }
              className={`px-2 py-1 rounded-lg text-[11px] font-bold flex items-center space-x-1 transition-colors ${
                channelFilter === 'pinned' || channelFilter === 'unpinned'
                  ? 'bg-brand-600 text-white'
                  : 'hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-600 dark:text-surface-400'
              }`}
              title={
                channelFilter === 'pinned'
                  ? 'Showing pinned chats - click for unpinned chats'
                  : channelFilter === 'unpinned'
                  ? 'Showing unpinned chats - click to show all chats'
                  : 'Show pinned chats'
              }
            >
              {channelFilter === 'unpinned' ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
              {(channelFilter === 'pinned' || channelFilter === 'unpinned') && (
                <span>{channelFilter === 'pinned' ? 'Pinned' : 'Unpinned'}</span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setChannelFilter(channelFilter === 'archived' ? 'all' : 'archived')}
              className={`px-2 py-1 rounded-lg text-[11px] font-bold flex items-center space-x-1 transition-colors ${
                channelFilter === 'archived'
                  ? 'bg-brand-600 text-white'
                  : 'hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-600 dark:text-surface-400'
              }`}
              title={channelFilter === 'archived' ? 'Back to Active Chats' : 'View Archived Chats'}
            >
              <Archive className="w-3.5 h-3.5" />
              <span>Archived{archivedCount > 0 ? ` (${archivedCount})` : ''}</span>
            </button>
            <button
              onClick={() => setIsNewChannelModalOpen(true)}
              className="p-1.5 rounded-full hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-600 hover:text-brand-600 transition-colors"
              title="New Conversation or Group"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Search & Category Filter Chips */}
        <div className="p-2.5 border-b border-surface-200/60 dark:border-surface-800 space-y-2 bg-white dark:bg-surface-900">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={chatSearchQuery}
              onChange={(e) => setChatSearchQuery(e.target.value)}
              placeholder="Search or start new chat"
              className="w-full pl-8 pr-3 py-1.5 bg-surface-100 dark:bg-surface-800/80 rounded-xl text-xs text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            {chatSearchQuery && (
              <button
                onClick={() => setChatSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-surface-400 hover:text-surface-600"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          <div className="flex items-center space-x-1 overflow-x-auto pb-1.5 text-[11px] font-semibold [scrollbar-width:thin]">
            {(['all', 'unread', 'direct', 'groups'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setChannelFilter(tab)}
                className={`px-2.5 py-1 rounded-full capitalize whitespace-nowrap shrink-0 transition-colors ${
                  channelFilter === tab
                    ? 'bg-brand-600 text-white font-bold shadow-xs'
                    : 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400 hover:bg-surface-200'
                }`}
              >
                {tab === 'all' ? 'All' : tab === 'direct' ? 'Direct' : tab === 'groups' ? 'Groups' : 'Unread'}
              </button>
            ))}
          </div>
        </div>

        {/* Conversation Cards Feed */}
        <div className="flex-1 overflow-y-auto divide-y divide-surface-100 dark:divide-surface-800/60">
          {filteredChannels.length === 0 && !channelsLoaded ? (
            <div className="p-8 flex items-center justify-center gap-2 text-xs text-surface-400">
              <div className="w-4 h-4 border-2 border-brand-500/30 border-t-brand-500 rounded-full animate-spin" />
              <span>Loading chats...</span>
            </div>
          ) : filteredChannels.length === 0 ? (
            <div className="p-8 text-center text-xs text-surface-400">
              No conversations match your filter.
            </div>
          ) : (
            filteredChannels.map((c) => {
              const isSelected = c.id === selectedChannelId;
              const isDM = c.type === 'direct';
              const otherUser = isDM ? (c.members || []).find((m) => m?.id !== user?.id) : null;
              const isOnline = otherUser ? isUserOnline(otherUser.id) : false;
              const displayName = otherUser?.fullName || c.name;

              return (
                <div
                  key={c.id}
                  onClick={() => setSelectedChannelId(c.id)}
                  className={`group/chat relative flex items-center justify-between px-3.5 py-3 cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-brand-50/70 dark:bg-brand-950/40 border-l-2 border-brand-500'
                      : 'hover:bg-surface-100/70 dark:hover:bg-surface-800/40 bg-white dark:bg-surface-900'
                  }`}
                >
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    {/* Avatar with Online Badge */}
                    <div className="relative shrink-0">
                      {isDM ? (
                        <Avatar
                          src={otherUser?.avatarUrl}
                          name={displayName}
                          size="md"
                          isOnline={isOnline}
                          userData={otherUser}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-brand-50 dark:bg-brand-950/60 text-brand-600 dark:text-brand-400 flex items-center justify-center font-bold text-xs border border-brand-500/20">
                          <Users className="w-5 h-5 stroke-[2]" />
                        </div>
                      )}
                    </div>

                    {/* Chat Info & Snippet */}
                    <div className="min-w-0 flex-1 pr-1">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-xs text-surface-900 dark:text-surface-100 truncate flex items-center space-x-1">
                          <span className="truncate">{displayName}</span>
                          {c.isMuted && (
                            <BellOff className="w-3 h-3 text-surface-400 shrink-0 ml-1" title="Muted" />
                          )}
                          {c.isPinned && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleChannelPin(c.id);
                              }}
                              title="Pinned - click to unpin"
                              className="shrink-0 ml-0.5 p-0.5 rounded hover:bg-surface-200 dark:hover:bg-surface-700"
                            >
                              <Pin className="w-3 h-3 text-brand-600 dark:text-brand-400 fill-brand-600" />
                            </button>
                          )}
                        </span>

                        <span className="text-[10px] text-surface-400 shrink-0">
                          {c.lastMessage?.createdAt ? formatMessageTime(c.lastMessage.createdAt) : ''}
                        </span>
                      </div>

                      <div className="flex items-center justify-between mt-0.5">
                        <span className="text-[11px] text-surface-500 dark:text-surface-400 truncate block">
                          {drafts[c.id] && c.id !== selectedChannelId ? (
                            <span>
                              <span className="font-semibold text-rose-500">Draft: </span>
                              {drafts[c.id]}
                            </span>
                          ) : c.lastMessage ? (
                            c.lastMessage.content.startsWith('🎤 Voice') ? (
                              <span className="flex items-center space-x-1 text-brand-600 dark:text-brand-400 font-medium">
                                <Mic className="w-3 h-3" />
                                <span>
                                  {!isDM && c.lastMessage.senderName
                                    ? `${c.lastMessage.senderName.split(' ')[0]}: Voice message`
                                    : 'Voice message'}
                                </span>
                              </span>
                            ) : !isDM && c.lastMessage.senderName ? (
                              <span>
                                <span className="font-semibold text-surface-700 dark:text-surface-300">
                                  {c.lastMessage.senderName.split(' ')[0]}:{' '}
                                </span>
                                {c.lastMessage.content}
                              </span>
                            ) : (
                              c.lastMessage.content
                            )
                          ) : (
                            <span className="italic opacity-60">No messages yet</span>
                          )}
                        </span>

                        {c.unreadCount !== undefined && c.unreadCount > 0 && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] font-black rounded-full bg-brand-600 text-white shrink-0 animate-in zoom-in-75">
                            {c.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* WhatsApp-Style Hover Dropdown Trigger */}
                  <div className="opacity-100 sm:opacity-0 sm:group-hover/chat:opacity-100 transition-opacity ml-1 flex items-center">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleChannelPin(c.id);
                      }}
                      title={c.isPinned ? 'Unpin chat' : 'Pin chat'}
                      className="flex items-center gap-1 px-1.5 py-1 rounded-full hover:bg-surface-200 dark:hover:bg-surface-700 text-surface-500 text-[10px] font-bold"
                    >
                      {c.isPinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                      <span className="hidden md:inline">{c.isPinned ? 'Unpin' : 'Pin'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveChannelMenuId(activeChannelMenuId === c.id ? null : c.id);
                      }}
                      className="p-1 rounded-full hover:bg-surface-200 dark:hover:bg-surface-700 text-surface-500"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Channel Context Dropdown Menu */}
                  {activeChannelMenuId === c.id && (
                    <div
                      className="absolute right-3 top-10 z-40 w-44 bg-white dark:bg-surface-800 rounded-xl shadow-elevated border border-surface-200 dark:border-surface-700 py-1 text-xs text-surface-700 dark:text-surface-200 animate-in fade-in zoom-in-95"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={() => handleToggleChannelPin(c.id)}
                        className="w-full text-left px-3 py-1.5 hover:bg-surface-100 dark:hover:bg-surface-700 flex items-center space-x-2"
                      >
                        <Pin className="w-3.5 h-3.5" />
                        <span>{c.isPinned ? 'Unpin chat' : 'Pin chat'}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleChannelMute(c.id)}
                        className="w-full text-left px-3 py-1.5 hover:bg-surface-100 dark:hover:bg-surface-700 flex items-center space-x-2"
                      >
                        <BellOff className="w-3.5 h-3.5" />
                        <span>{c.isMuted ? 'Unmute' : 'Mute notifications'}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMarkChannelUnread(c.id)}
                        className="w-full text-left px-3 py-1.5 hover:bg-surface-100 dark:hover:bg-surface-700 flex items-center space-x-2"
                      >
                        <Check className="w-3.5 h-3.5" />
                        <span>Mark as unread</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleChannelArchive(c.id)}
                        className="w-full text-left px-3 py-1.5 hover:bg-surface-100 dark:hover:bg-surface-700 flex items-center space-x-2"
                      >
                        <Archive className="w-3.5 h-3.5" />
                        <span>{c.isArchived ? 'Unarchive' : 'Archive chat'}</span>
                      </button>
                      {c.type !== 'direct' && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedChannelId(c.id);
                            setIsAddMembersModalOpen(true);
                            setActiveChannelMenuId(null);
                          }}
                          className="w-full text-left px-3 py-1.5 hover:bg-brand-50 dark:hover:bg-brand-950/40 flex items-center space-x-2 text-brand-600 dark:text-brand-400 font-semibold border-t border-surface-100 dark:border-surface-700 mt-1 pt-1.5"
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                          <span>Add members</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ----------------- CENTER CONVERSATION VIEW ----------------- */}
      <div
        className={`${
          selectedChannelId ? 'flex' : 'hidden md:flex'
        } flex-1 flex flex-col bg-[#efeae2]/30 dark:bg-[#0b141a]/60 min-w-0 relative`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDraggingOverChat(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDraggingOverChat(false);
        }}
        onDrop={handleChatDrop}
      >
        {/* Drag & Drop Overlay */}
        {isDraggingOverChat && (
          <div className="absolute inset-0 bg-brand-500/10 backdrop-blur-[2px] border-2 border-dashed border-brand-500 rounded-3xl z-30 flex flex-col items-center justify-center p-6 pointer-events-none">
            <div className="p-4 rounded-full bg-white dark:bg-surface-800 shadow-elevated text-brand-600 mb-2">
              <UploadCloud className="w-8 h-8" />
            </div>
            <span className="font-bold text-sm text-surface-900 dark:text-surface-100">
              Drop files here to attach to chat or task
            </span>
          </div>
        )}

        {currentChannel ? (
          (() => {
            const otherUser =
              currentChannel.type === 'direct'
                ? (currentChannel.members || []).find((m) => m?.id !== user?.id) ||
                  (teamMembers || []).find((m) => m?.id !== user?.id)
                : null;
              const displayName = otherUser?.fullName || currentChannel.name;
              const isOnline = otherUser ? isUserOnline(otherUser.id) : false;

              return (
                <>
                  {/* Conversation Header Bar */}
                  <div className="h-16 px-3.5 sm:px-6 bg-white dark:bg-surface-900 border-b border-surface-200 dark:border-surface-800 flex items-center justify-between shrink-0 shadow-xs z-20">
                    <div className="flex items-center space-x-2.5 sm:space-x-3 min-w-0">
                      {/* Mobile Back Button */}
                      <button
                        onClick={() => setSelectedChannelId(null)}
                        className="md:hidden p-1.5 -ml-1 text-surface-500 hover:text-surface-900 dark:hover:text-surface-100 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-full transition-colors shrink-0"
                        aria-label="Back to conversations"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>

                      <div
                        className={`flex items-center space-x-2.5 min-w-0 ${otherUser ? 'cursor-pointer group' : ''}`}
                        onClick={() => otherUser && handleOpenProfileDrawer(otherUser)}
                        title={otherUser ? `View ${otherUser.fullName}'s profile` : undefined}
                      >
                        {currentChannel.type === 'direct' ? (
                          <Avatar
                            src={otherUser?.avatarUrl}
                            name={displayName}
                            size="sm"
                            isOnline={isOnline}
                            userData={otherUser}
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-brand-50 dark:bg-brand-950/60 text-brand-600 dark:text-brand-400 flex items-center justify-center font-bold text-xs shrink-0">
                            <Users className="w-4 h-4" />
                          </div>
                        )}

                        <div className="min-w-0">
                          <div className="flex items-center space-x-1.5">
                            <h3 className="font-bold text-xs sm:text-sm text-surface-900 dark:text-surface-100 leading-tight truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                              {displayName}
                            </h3>
                            {/* ⭐ ClickUp Star / Favorite Toggle */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleChannelPin(currentChannel.id);
                              }}
                              className={`p-1 rounded-full transition-colors ${
                                currentChannel.isPinned
                                  ? 'text-amber-500 hover:text-amber-600'
                                  : 'text-surface-300 hover:text-amber-500'
                              }`}
                              title={currentChannel.isPinned ? 'Starred / Favorited' : 'Star / Favorite this conversation'}
                            >
                              <Star className={`w-3.5 h-3.5 ${currentChannel.isPinned ? 'fill-amber-400' : ''}`} />
                            </button>
                          </div>

                          {currentChannel.type === 'direct' ? (
                            <span className="text-[10px] text-surface-400 block truncate">
                              {isOnline ? 'online' : 'offline'}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setIsMembersDrawerOpen(true)}
                              className="text-[10px] text-surface-400 hover:text-brand-600 dark:hover:text-brand-400 block truncate text-left transition-colors"
                              title="Click to view and manage channel members"
                            >
                              {(currentChannel.members || []).length} participants • View & Add
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Header Right Action Icons */}
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      {/* Search in chat toggle */}
                      <button
                        type="button"
                        onClick={() => setIsMessageSearchOpen(!isMessageSearchOpen)}
                        className={`p-2 rounded-full transition-colors ${
                          isMessageSearchOpen
                            ? 'bg-brand-50 text-brand-600 dark:bg-brand-950/40'
                            : 'hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-500'
                        }`}
                        title="Search messages in conversation"
                      >
                        <Search className="w-4 h-4" />
                      </button>

                      {/* Video Call */}
                      <button
                        type="button"
                        onClick={() => {
                          if (currentChannel.type === 'direct' && otherUser) {
                            startDirectCall(
                              {
                                id: otherUser.id,
                                name: otherUser.fullName,
                                avatarUrl: otherUser.avatarUrl || undefined,
                                role: otherUser.title || otherUser.role,
                              },
                              true,
                              currentChannel.id
                            );
                          } else {
                            startGroupCall({ id: currentChannel.id, name: currentChannel.name }, true);
                          }
                        }}
                        className="p-2 rounded-full hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-500 transition-colors"
                        title="Start Video Call"
                      >
                        <Video className="w-4 h-4 text-brand-600 dark:text-brand-400" />
                      </button>

                      {/* Audio Call (starts audio-only, convertible to video call anytime) */}
                      <button
                        type="button"
                        onClick={() => {
                          if (currentChannel.type === 'direct' && otherUser) {
                            startDirectCall(
                              {
                                id: otherUser.id,
                                name: otherUser.fullName,
                                avatarUrl: otherUser.avatarUrl || undefined,
                                role: otherUser.title || otherUser.role,
                              },
                              false,
                              currentChannel.id
                            );
                          } else {
                            startGroupCall({ id: currentChannel.id, name: currentChannel.name }, false);
                          }
                        }}
                        className="p-2 rounded-full hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-500 transition-colors"
                        title="Start Audio Call"
                      >
                        <PhoneCall className="w-4 h-4" />
                      </button>

                      {/* Show Profile Button (ClickUp Docked Drawer) */}
                      {otherUser && (
                        <button
                          type="button"
                          onClick={() => handleOpenProfileDrawer(otherUser)}
                          className={`flex items-center space-x-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border shadow-xs shrink-0 ${
                            isProfileDrawerOpen
                              ? 'bg-brand-50 dark:bg-brand-950/60 border-brand-500/40 text-brand-600 dark:text-brand-400'
                              : 'bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-200 border-surface-200/60 dark:border-surface-700/60'
                          }`}
                          title="Show profile & lifetime activity"
                        >
                          <UserIcon className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
                          <span className="hidden sm:inline">Show profile</span>
                        </button>
                      )}

                      {/* Add Members Direct Action Button */}
                      {currentChannel.type !== 'direct' && (
                        <button
                          type="button"
                          onClick={() => setIsAddMembersModalOpen(true)}
                          className="flex items-center space-x-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-bold bg-brand-50 hover:bg-brand-100 dark:bg-brand-950/60 dark:hover:bg-brand-900/60 text-brand-600 dark:text-brand-400 border border-brand-200 dark:border-brand-800/60 shadow-xs transition-all shrink-0"
                          title="Add members to this group channel"
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Add Members</span>
                        </button>
                      )}

                      {/* Group Members Drawer Toggle */}
                      {currentChannel.type !== 'direct' && (
                        <button
                          type="button"
                          onClick={() => setIsMembersDrawerOpen(!isMembersDrawerOpen)}
                          className="p-2 rounded-full hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-500 transition-colors"
                          title="Channel Members"
                        >
                          <Users className="w-4 h-4" />
                        </button>
                      )}

                      {/* Pop Out to Floating Chatbox */}
                      <button
                        type="button"
                        onClick={() => {
                          if (selectedChannelId) {
                            window.dispatchEvent(
                              new CustomEvent('open-floating-chat', {
                                detail: { channelId: selectedChannelId },
                              })
                            );
                            onPopOut?.(selectedChannelId);
                          }
                        }}
                        className="flex items-center space-x-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-surface-100 hover:bg-brand-50 hover:border-brand-300 dark:bg-surface-800 dark:hover:bg-brand-950/60 text-surface-700 hover:text-brand-600 dark:text-surface-200 dark:hover:text-brand-400 text-xs font-semibold border border-surface-200/80 dark:border-surface-700 transition-all shadow-xs ml-1 shrink-0"
                        title="Make chat into floating chatbox so you can do other things in the app"
                      >
                        <PictureInPicture className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
                        <span className="hidden sm:inline">Pop Out</span>
                      </button>

                      {/* Close Chat Button */}
                      <button
                        type="button"
                        onClick={() => setSelectedChannelId(null)}
                        className="p-2 rounded-full hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 transition-colors ml-1"
                        title="Close Chat (Esc)"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* ClickUp View Mode Tabs: Chat | Calendar | Tasks | + View */}
                  <div className="flex items-center space-x-1 px-3 sm:px-6 py-1 bg-surface-50/80 dark:bg-surface-900/80 border-b border-surface-200/80 dark:border-surface-800 text-xs shrink-0">
                    <button
                      type="button"
                      onClick={() => setActiveHeaderTab('chat')}
                      className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg font-bold transition-all ${
                        activeHeaderTab === 'chat'
                          ? 'bg-white dark:bg-surface-800 text-brand-600 dark:text-brand-400 shadow-xs border border-surface-200/60 dark:border-surface-700'
                          : 'text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-surface-200'
                      }`}
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      <span>Chat</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveHeaderTab('calendar')}
                      className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg font-bold transition-all ${
                        activeHeaderTab === 'calendar'
                          ? 'bg-white dark:bg-surface-800 text-brand-600 dark:text-brand-400 shadow-xs border border-surface-200/60 dark:border-surface-700'
                          : 'text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-surface-200'
                      }`}
                    >
                      <Calendar className="w-3.5 h-3.5" />
                      <span>Calendar</span>
                      {channelMeetings.length > 0 && (
                        <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-brand-100 text-brand-700 dark:bg-brand-950/60 dark:text-brand-300">
                          {channelMeetings.length}
                        </span>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveHeaderTab('tasks')}
                      className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg font-bold transition-all ${
                        activeHeaderTab === 'tasks'
                          ? 'bg-white dark:bg-surface-800 text-brand-600 dark:text-brand-400 shadow-xs border border-surface-200/60 dark:border-surface-700'
                          : 'text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-surface-200'
                      }`}
                    >
                      <ListChecks className="w-3.5 h-3.5" />
                      <span>Tasks</span>
                      {channelTasks.length > 0 && (
                        <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-brand-100 text-brand-700 dark:bg-brand-950/60 dark:text-brand-300">
                          {channelTasks.length}
                        </span>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        if (activeHeaderTab === 'chat') setActiveHeaderTab('tasks');
                        else if (activeHeaderTab === 'tasks') setActiveHeaderTab('calendar');
                        else setActiveHeaderTab('chat');
                      }}
                      className="flex items-center space-x-1 px-2.5 py-1.5 rounded-lg font-medium text-surface-400 hover:text-surface-600 dark:hover:text-surface-300 text-[11px] hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                      title="Cycle views"
                    >
                      <Plus className="w-3 h-3" />
                      <span>View</span>
                    </button>
                  </div>

                  {activeHeaderTab === 'tasks' ? (
                    /* ----------------- EMBEDDED CHANNEL TASKS VIEW ----------------- */
                    <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-surface-50/50 dark:bg-surface-950/40">
                      <div className="max-w-4xl mx-auto space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white dark:bg-surface-900 p-3.5 rounded-2xl border border-surface-200 dark:border-surface-800 shadow-xs">
                          <div className="flex items-center space-x-1 overflow-x-auto no-scrollbar text-xs font-semibold">
                            {(['all', 'todo', 'in_progress', 'done'] as const).map((st) => (
                              <button
                                key={st}
                                type="button"
                                onClick={() => setTaskStatusFilter(st)}
                                className={`px-3 py-1.5 rounded-xl capitalize whitespace-nowrap transition-colors ${
                                  taskStatusFilter === st
                                    ? 'bg-brand-600 text-white font-bold shadow-xs'
                                    : 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400 hover:bg-surface-200'
                                }`}
                              >
                                {st === 'all' ? 'All Tasks' : st.replace('_', ' ')}
                              </button>
                            ))}
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setDraftTaskTitle('');
                              setDraftTaskDescription('');
                              setIsTaskModalOpen(true);
                            }}
                            className="px-3.5 py-1.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs flex items-center justify-center space-x-1.5 shadow-sm transition-all shrink-0"
                          >
                            <Plus className="w-4 h-4" />
                            <span>New Task</span>
                          </button>
                        </div>

                        {isLoadingChannelTasks ? (
                          <div className="flex justify-center py-16">
                            <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
                          </div>
                        ) : (() => {
                          const filtered = channelTasks.filter((t) => {
                            if (taskStatusFilter === 'all') return true;
                            return t.status === taskStatusFilter;
                          });

                          if (filtered.length === 0) {
                            return (
                              <div className="p-12 text-center bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800">
                                <CheckSquare className="w-10 h-10 text-surface-300 mx-auto mb-2" />
                                <h4 className="font-bold text-xs text-surface-700 dark:text-surface-300">
                                  No tasks match this filter
                                </h4>
                                <p className="text-[11px] text-surface-400 mt-1">
                                  Create a task to coordinate deliverables with {displayName}.
                                </p>
                              </div>
                            );
                          }

                          return (
                            <div className="space-y-2.5">
                              {filtered.map((t) => (
                                <div
                                  key={t.id}
                                  onClick={() => {
                                    window.dispatchEvent(
                                      new CustomEvent('open-task-modal', { detail: { taskId: t.id } })
                                    );
                                  }}
                                  className="p-3.5 rounded-2xl bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 hover:border-brand-500/60 shadow-xs hover:shadow-card transition-all cursor-pointer group flex items-center justify-between"
                                >
                                  <div className="min-w-0 flex-1 pr-3">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <span className="font-mono text-[10px] font-black px-1.5 py-0.5 rounded bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400 border border-surface-200 dark:border-surface-700">
                                        {t.code || 'TSK'}
                                      </span>
                                      <span className="font-bold text-xs text-surface-900 dark:text-surface-100 truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                                        {t.title}
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-2 text-[10px] text-surface-400">
                                      <span>Due: {t.dueDate || 'No due date'}</span>
                                      <span>•</span>
                                      <span>Project: {t.projectName || 'General'}</span>
                                    </div>
                                  </div>

                                  <div className="flex items-center space-x-2 shrink-0">
                                    <span
                                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                        t.status === 'done'
                                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                                          : t.status === 'in_progress'
                                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300'
                                          : 'bg-surface-100 text-surface-700 dark:bg-surface-800 dark:text-surface-300'
                                      }`}
                                    >
                                      {t.status.replace('_', ' ')}
                                    </span>
                                    <Avatar
                                      src={t.assigneeAvatar}
                                      name={t.assigneeName || 'Unassigned'}
                                      size="xs"
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ) : activeHeaderTab === 'calendar' ? (
                    /* ----------------- EMBEDDED CHANNEL CALENDAR VIEW ----------------- */
                    <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-surface-50/50 dark:bg-surface-950/40">
                      <div className="max-w-4xl mx-auto space-y-4">
                        <div className="flex items-center justify-between bg-white dark:bg-surface-900 p-3.5 rounded-2xl border border-surface-200 dark:border-surface-800 shadow-xs">
                          <div>
                            <h3 className="font-bold text-xs sm:text-sm text-surface-900 dark:text-surface-100">
                              SyncUps & Meetings
                            </h3>
                            <p className="text-[11px] text-surface-400">
                              Scheduled 1-on-1s and video discussions with {displayName}
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setMeetingModalTitle(`1-on-1 with ${displayName}`);
                              setMeetingModalDate(new Date().toISOString().split('T')[0]);
                              setIsScheduleMeetingModalOpen(true);
                            }}
                            className="px-3.5 py-1.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs flex items-center space-x-1.5 shadow-sm transition-all shrink-0"
                          >
                            <Calendar className="w-4 h-4" />
                            <span>Book Meeting</span>
                          </button>
                        </div>

                        {isLoadingChannelMeetings ? (
                          <div className="flex justify-center py-16">
                            <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
                          </div>
                        ) : channelMeetings.length === 0 ? (
                          <div className="p-12 text-center bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800">
                            <Calendar className="w-10 h-10 text-surface-300 mx-auto mb-2" />
                            <h4 className="font-bold text-xs text-surface-700 dark:text-surface-300">
                              No scheduled meetings
                            </h4>
                            <p className="text-[11px] text-surface-400 mt-1">
                              Plan a SyncUp to meet or grab some coffee together.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {channelMeetings.map((m: any) => (
                              <div
                                key={m.id}
                                className="p-4 rounded-2xl bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 shadow-xs flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-brand-50 text-brand-700 dark:bg-brand-950/60 dark:text-brand-300">
                                      {m.durationMinutes} mins
                                    </span>
                                    <h4 className="font-bold text-xs sm:text-sm text-surface-900 dark:text-surface-100 truncate">
                                      {m.title}
                                    </h4>
                                  </div>
                                  <div className="flex items-center space-x-2 text-[11px] text-surface-500">
                                    <Clock className="w-3.5 h-3.5 text-surface-400" />
                                    <span>
                                      {new Date(m.scheduledAt).toLocaleString([], {
                                        weekday: 'short',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </span>
                                  </div>
                                  {m.description && (
                                    <p className="text-xs text-surface-600 dark:text-surface-300 mt-1 line-clamp-1">
                                      {m.description}
                                    </p>
                                  )}
                                </div>

                                <div className="flex items-center space-x-2 shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (currentChannel.type === 'direct' && otherUser) {
                                        startDirectCall({
                                          id: otherUser.id,
                                          name: otherUser.fullName,
                                          avatarUrl: otherUser.avatarUrl || undefined,
                                          role: otherUser.role,
                                        });
                                      } else {
                                        startGroupCall({ id: currentChannel.id, name: currentChannel.name });
                                      }
                                    }}
                                    className="px-3.5 py-1.5 rounded-xl bg-brand-50 hover:bg-brand-100 dark:bg-brand-950/50 dark:hover:bg-brand-900/50 text-brand-600 dark:text-brand-400 font-bold text-xs flex items-center space-x-1.5 transition-colors border border-brand-200 dark:border-brand-800"
                                  >
                                    <Video className="w-3.5 h-3.5" />
                                    <span>Start Call</span>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* ----------------- STANDARD CHAT CONVERSATION VIEW ----------------- */
                    <>
                      {/* In-Chat Message Search Bar */}
                      {isMessageSearchOpen && (
                        <div className="px-4 py-2 bg-surface-50 dark:bg-surface-800 border-b border-surface-200 dark:border-surface-700 flex items-center justify-between text-xs animate-in slide-in-from-top-2">
                          <div className="relative flex-1 mr-3">
                            <Search className="w-3.5 h-3.5 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                              type="text"
                              value={messageSearchQuery}
                              onChange={(e) => setMessageSearchQuery(e.target.value)}
                              placeholder="Search in this chat..."
                              autoFocus
                              className="w-full pl-8 pr-3 py-1 bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-700 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
                            />
                          </div>
                          <div className="flex items-center space-x-2 text-surface-500">
                            <span className="text-[11px] font-semibold">
                              {messageSearchQuery ? `${visibleMessages.length} match(es)` : ''}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setIsMessageSearchOpen(false);
                                setMessageSearchQuery('');
                              }}
                              className="p-1 hover:text-surface-900"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Pinned Messages Header Strip */}
                      {pinnedMessages.length > 0 && currentPinnedMsg && (
                        <div className="px-4 py-2 bg-amber-50/95 dark:bg-amber-950/60 border-b border-amber-200/90 dark:border-amber-800/80 flex items-center justify-between text-xs text-amber-900 dark:text-amber-200 shadow-xs z-10 transition-all">
                          <button
                            type="button"
                            onClick={() => scrollToMessage(currentPinnedMsg.id)}
                            className="flex items-center space-x-2 truncate hover:opacity-80 transition-opacity text-left cursor-pointer group flex-1 mr-3 min-w-0"
                            title="Click to jump to this pinned message in the conversation"
                          >
                            <div className="p-1 rounded-full bg-amber-200/70 dark:bg-amber-900/60 text-amber-700 dark:text-amber-300 group-hover:scale-110 transition-transform shrink-0">
                              <Pin className="w-3.5 h-3.5 fill-amber-600 text-amber-600" />
                            </div>
                            <div className="truncate flex-1 min-w-0">
                              <span className="font-bold text-amber-800 dark:text-amber-300 mr-1.5">
                                Pinned:
                              </span>
                              <span className="font-medium truncate opacity-90">
                                "{currentPinnedMsg.content ? currentPinnedMsg.content.slice(0, 70) : (currentPinnedMsg.attachments?.length ? `Attachment (${currentPinnedMsg.attachments[0].fileName})` : 'Pinned message')}"
                              </span>
                            </div>
                          </button>

                          <div className="flex items-center space-x-2 shrink-0">
                            {pinnedMessages.length > 1 && (
                              <button
                                type="button"
                                onClick={() => setActivePinnedIndex((prev) => (prev + 1) % pinnedMessages.length)}
                                className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 hover:bg-amber-200/80 transition-colors cursor-pointer"
                                title="Click to view next pinned message"
                              >
                                {safePinnedIndex + 1} of {pinnedMessages.length} Pinned
                              </button>
                            )}
                            {pinnedMessages.length === 1 && (
                              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50">
                                1 Pinned
                              </span>
                            )}

                            {/* Direct Unpin Action Button */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTogglePin(currentPinnedMsg.id, false);
                              }}
                              className="flex items-center space-x-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/60 dark:hover:bg-amber-800/80 text-amber-900 dark:text-amber-100 transition-colors shadow-xs active:scale-95 cursor-pointer"
                              title="Unpin this message"
                            >
                              <PinOff className="w-3.5 h-3.5 text-amber-700 dark:text-amber-300" />
                              <span className="hidden xs:inline">Unpin</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Messages Feed */}
                      <div
                        ref={messagesContainerRef}
                        onScroll={handleScroll}
                        className={`flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-5 space-y-2.5 select-text relative ${
                          messages.length > 0 && scrolledChannelId !== selectedChannelId ? 'opacity-0' : 'transition-opacity duration-100'
                        }`}
                      >
                        {/* Loading Skeleton */}
                        {isLoadingMessages && messages.length === 0 && (
                          <div className="p-4 sm:p-6 space-y-4 animate-pulse">
                            <div className="flex items-start space-x-3">
                              <div className="w-8 h-8 rounded-full bg-surface-200 dark:bg-surface-800 shrink-0" />
                              <div className="space-y-2 flex-1 max-w-sm">
                                <div className="h-3.5 bg-surface-200 dark:bg-surface-800 rounded-md w-1/4" />
                                <div className="h-10 bg-surface-200 dark:bg-surface-800 rounded-2xl w-3/4" />
                              </div>
                            </div>
                            <div className="flex items-start justify-end space-x-3">
                              <div className="space-y-2 flex-1 max-w-sm flex flex-col items-end">
                                <div className="h-12 bg-brand-200/40 dark:bg-brand-900/30 rounded-2xl w-2/3" />
                              </div>
                            </div>
                            <div className="flex items-start space-x-3">
                              <div className="w-8 h-8 rounded-full bg-surface-200 dark:bg-surface-800 shrink-0" />
                              <div className="space-y-2 flex-1 max-w-sm">
                                <div className="h-8 bg-surface-200 dark:bg-surface-800 rounded-2xl w-1/2" />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* ClickUp Hero "Get Started" Banner - ONLY ON EMPTY / NEW CHATS */}
                        {!isLoadingMessages && messages.length === 0 && (
                          <div className="mb-6 p-4 sm:p-5 rounded-2xl bg-white dark:bg-surface-900/90 border border-surface-200/90 dark:border-surface-800 shadow-xs">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                              <div className="flex items-center space-x-3.5">
                                {currentChannel.type === 'direct' && otherUser ? (
                                  <div className="relative">
                                    <Avatar
                                      src={otherUser.avatarUrl}
                                      name={otherUser.fullName}
                                      size="lg"
                                      isOnline={isOnline}
                                      userData={otherUser}
                                    />
                                  </div>
                                ) : (
                                  <div className="w-12 h-12 rounded-2xl bg-brand-50 dark:bg-brand-950/60 text-brand-600 dark:text-brand-400 flex items-center justify-center font-bold text-base border border-brand-500/20 shadow-xs">
                                    <Users className="w-6 h-6 stroke-[2]" />
                                  </div>
                                )}

                                <div>
                                  <h2 className="text-base sm:text-lg font-black text-surface-900 dark:text-surface-100 tracking-tight">
                                    Get started with {displayName}
                                  </h2>
                                  <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">
                                    Choose an action below to collaborate quickly or jump straight into chatting.
                                  </p>
                                </div>
                              </div>

                              {otherUser && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenProfileDrawer(otherUser)}
                                  className="inline-flex items-center space-x-1.5 px-3.5 py-1.5 rounded-xl bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-200 text-xs font-bold transition-all shadow-xs shrink-0 self-start sm:self-center border border-surface-200/80 dark:border-surface-700/80"
                                  title="View full profile and lifetime activity"
                                >
                                  <UserIcon className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
                                  <span>View Profile</span>
                                </button>
                              )}
                            </div>

                            {/* Pastel Action Cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {/* Card 1: View Calendar / Schedule Sync */}
                              <div
                                onClick={() => {
                                  setActiveHeaderTab('calendar');
                                  setIsScheduleMeetingModalOpen(true);
                                }}
                                className="p-3.5 rounded-xl bg-indigo-50/70 hover:bg-indigo-100/70 dark:bg-indigo-950/30 dark:hover:bg-indigo-900/40 border border-indigo-100 dark:border-indigo-900/50 cursor-pointer transition-all hover:scale-[1.01] active:scale-[0.99] group shadow-xs"
                              >
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                                    <Calendar className="w-5 h-5 stroke-[2.2]" />
                                  </div>
                                  <div className="min-w-0">
                                    <h4 className="text-xs font-bold text-indigo-950 dark:text-indigo-200 leading-snug">
                                      View Calendar
                                    </h4>
                                    <p className="text-[11px] text-indigo-700/80 dark:text-indigo-300/80 truncate">
                                      Find time to meet or just grab some coffee
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Card 2: Start SyncUp */}
                              <div
                                onClick={() => {
                                  if (currentChannel.type === 'direct' && otherUser) {
                                    startDirectCall({
                                      id: otherUser.id,
                                      name: otherUser.fullName,
                                      avatarUrl: otherUser.avatarUrl || undefined,
                                      role: otherUser.role,
                                    });
                                  } else {
                                    startGroupCall({ id: currentChannel.id, name: currentChannel.name });
                                  }
                                }}
                                className="p-3.5 rounded-xl bg-emerald-50/70 hover:bg-emerald-100/70 dark:bg-emerald-950/30 dark:hover:bg-emerald-900/40 border border-emerald-100 dark:border-emerald-900/50 cursor-pointer transition-all hover:scale-[1.01] active:scale-[0.99] group shadow-xs"
                              >
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                                    <PhoneCall className="w-5 h-5 stroke-[2.2]" />
                                  </div>
                                  <div className="min-w-0">
                                    <h4 className="text-xs font-bold text-emerald-950 dark:text-emerald-200 leading-snug">
                                      Start SyncUp
                                    </h4>
                                    <p className="text-[11px] text-emerald-700/80 dark:text-emerald-300/80 truncate">
                                      Jump on a voice call or video call
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Floating Quick Tools Sidebar on Chat Margin */}
                        <div className="absolute right-3 top-24 z-10 hidden sm:flex flex-col items-center bg-white/90 dark:bg-surface-800/90 backdrop-blur-md border border-surface-200/80 dark:border-surface-700/80 rounded-2xl shadow-elevated p-1 space-y-1.5 transition-all">
                          <button
                            type="button"
                            onClick={() => setIsMessageSearchOpen(!isMessageSearchOpen)}
                            className={`p-2 rounded-xl text-surface-500 hover:text-brand-600 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors ${
                              isMessageSearchOpen ? 'text-brand-600 bg-brand-50 dark:bg-brand-950/60' : ''
                            }`}
                            title="Search in conversation"
                          >
                            <Search className="w-4 h-4" />
                          </button>

                          {otherUser && (
                            <button
                              type="button"
                              onClick={() => handleOpenProfileDrawer(otherUser)}
                              className={`p-2 rounded-xl text-surface-500 hover:text-brand-600 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors ${
                                isProfileDrawerOpen ? 'text-brand-600 bg-brand-50 dark:bg-brand-950/60' : ''
                              }`}
                              title="Toggle User Profile Drawer"
                            >
                              <UserIcon className="w-4 h-4" />
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => {
                              setMeetingModalTitle(`1-on-1 with ${displayName}`);
                              setMeetingModalDate(new Date().toISOString().split('T')[0]);
                              setIsScheduleMeetingModalOpen(true);
                            }}
                            className="p-2 rounded-xl text-surface-500 hover:text-brand-600 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
                            title="Schedule 1-on-1 / Meeting"
                          >
                            <Calendar className="w-4 h-4" />
                          </button>
                        </div>

                        {visibleMessages.map((msg, idx) => {
                          const isMe = msg.senderId === user?.id;
                const prevMsg = idx > 0 ? visibleMessages[idx - 1] : null;
                const showDate =
                  idx === 0 ||
                  formatDateSeparator(msg.createdAt) !== formatDateSeparator(prevMsg?.createdAt);

                return (
                  <React.Fragment key={msg.id}>
                    {/* WhatsApp-Style Date Separator Badge */}
                    {showDate && (
                      <div className="flex justify-center my-2.5">
                        <span className="px-3 py-0.5 rounded-full text-[10px] font-bold bg-white/90 dark:bg-surface-800 border border-surface-200/60 dark:border-surface-700/60 text-surface-600 dark:text-surface-300 shadow-xs uppercase tracking-wider select-none">
                          {formatDateSeparator(msg.createdAt)}
                        </span>
                      </div>
                    )}

                    {/* Message Row */}
                    <div
                      id={`chat-msg-${msg.id}`}
                      className={`flex items-end space-x-2 group/row min-w-0 rounded-2xl transition-colors duration-500 ${isMe ? 'justify-end' : 'justify-start'} ${
                        highlightedMessageId === msg.id ? 'bg-amber-100/70 dark:bg-amber-900/30 ring-2 ring-amber-400/70' : ''
                      }`}
                    >
                      {!isMe && (
                        <div
                          className="cursor-pointer hover:opacity-80 transition-opacity shrink-0"
                          onClick={() => openUserProfile(msg.senderId)}
                          title={`View ${msg.senderName}'s profile`}
                        >
                          <Avatar
                            src={msg.senderAvatar}
                            name={msg.senderName}
                            size="xs"
                            userId={msg.senderId}
                          />
                        </div>
                      )}

                      <div className={`max-w-[85%] sm:max-w-[70%] min-w-0 ${isMe ? 'items-end' : 'items-start'} flex flex-col relative`}>
                        {/* Sender Name in Group Chat */}
                        {!isMe && currentChannel?.type !== 'direct' && (
                          <div
                            onClick={() => openUserProfile(msg.senderId)}
                            className="flex items-center space-x-1.5 text-[11px] mb-0.5 px-1 font-bold text-brand-600 dark:text-brand-400 cursor-pointer hover:underline"
                            title={`View ${msg.senderName}'s profile`}
                          >
                            <span>{msg.senderName}</span>
                            <RoleBadge role={msg.senderRole} />
                          </div>
                        )}

                        {/* Message Bubble (IHB Pulse Brand Blue Theme) */}
                        <div
                          onClick={() => setActiveMobileMessageId(activeMobileMessageId === msg.id ? null : msg.id)}
                          className={`p-2.5 sm:p-3 rounded-2xl text-xs leading-relaxed relative shadow-xs transition-all max-w-full break-words ${
                            isMe
                              ? 'bg-brand-50 dark:bg-brand-950/50 text-surface-900 dark:text-surface-100 rounded-tr-xs border border-brand-200 dark:border-brand-800/60'
                              : 'bg-white dark:bg-surface-800 text-surface-900 dark:text-surface-100 rounded-tl-xs border border-surface-200/80 dark:border-surface-700/60'
                          }`}
                        >
                          {/* Forwarded Header */}
                          {msg.forwardedFromId && (
                            <div className="flex items-center space-x-1 text-[10px] italic opacity-60 mb-1">
                              <ChevronsRight className="w-2.5 h-2.5" />
                              <span>Forwarded</span>
                            </div>
                          )}

                          {/* Quoted / Replied Bubble Preview */}
                          {msg.replyToMessage && (
                            <div className="mb-2 text-[11px] px-2.5 py-1.5 rounded-lg bg-black/5 dark:bg-black/25 border-l-4 border-brand-500 text-surface-700 dark:text-surface-300 truncate max-w-full">
                              <span className="font-bold text-brand-600 dark:text-brand-400 block">
                                {msg.replyToMessage.senderName}
                              </span>
                              <span className="truncate block opacity-90">
                                {msg.replyToMessage.content}
                              </span>
                            </div>
                          )}

                          {/* Voice Note Player if Voice Message */}
                          {msg.voiceNote && msg.voiceNote.audioUrl && (
                            <div className="mb-1.5">
                              <VoiceNotePlayer
                                audioUrl={msg.voiceNote.audioUrl}
                                durationSeconds={msg.voiceNote.durationSeconds}
                                isMe={isMe}
                              />
                            </div>
                          )}

                          {/* Attachments / Images Rendering */}
                          {msg.attachments && msg.attachments.length > 0 && (
                            <div className="space-y-2 mb-1.5">
                              {msg.attachments.map((att, attIdx) => {
                                const isImage = isImageFile(att.fileName, att.mimeType);
                                const previewUrl = getAttachmentPreviewUrl(att);
                                const downloadUrl = getAttachmentDownloadUrl(att);

                                return isImage ? (
                                  <div
                                    key={attIdx}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveLightbox({
                                        url: previewUrl,
                                        downloadUrl,
                                        fileName: att.fileName,
                                        senderName: msg.senderName,
                                        createdAt: msg.createdAt,
                                      });
                                    }}
                                    className="relative group/img cursor-pointer overflow-hidden rounded-xl bg-black/5 dark:bg-black/20 border border-black/10 dark:border-white/10 max-w-sm transition-transform active:scale-[0.99]"
                                    title="Tap to expand image"
                                  >
                                    <img
                                      src={previewUrl}
                                      alt={att.fileName}
                                      loading="lazy"
                                      className="w-full max-h-72 object-cover rounded-xl transition-all duration-200"
                                      onError={(e) => {
                                        const target = e.currentTarget;
                                        if (downloadUrl && target.src !== downloadUrl) {
                                          target.src = downloadUrl;
                                        }
                                      }}
                                    />
                                    <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                                      <div className="p-2 rounded-full bg-black/60 text-white backdrop-blur-xs">
                                        <ZoomIn className="w-4 h-4" />
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <a
                                    key={attIdx}
                                    href={downloadUrl}
                                    download={att.fileName}
                                    onClick={(e) => e.stopPropagation()}
                                    className={`flex items-center space-x-2.5 p-2 rounded-xl transition-colors border ${
                                      isMe
                                        ? 'bg-black/5 hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/15 border-black/10 dark:border-white/10 text-inherit'
                                        : 'bg-surface-50 hover:bg-surface-100 dark:bg-surface-700/60 dark:hover:bg-surface-700 border-surface-200 dark:border-surface-600 text-surface-900 dark:text-surface-100'
                                    }`}
                                  >
                                    <FileText className="w-4 h-4 shrink-0 text-brand-500" />
                                    <div className="flex-1 min-w-0">
                                      <span className="font-semibold truncate block text-xs">
                                        {att.fileName}
                                      </span>
                                      <span className="text-[10px] opacity-75">
                                        {(att.fileSize / 1024).toFixed(0)} KB
                                      </span>
                                    </div>
                                    <Download className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 shrink-0" />
                                  </a>
                                );
                              })}
                            </div>
                          )}

                          {/* Message Text Content */}
                          {(!msg.voiceNote || msg.content !== '🎤 Voice message') &&
                            (!msg.attachments || msg.content !== 'Photo') &&
                            msg.content && (
                              <div className={`whitespace-pre-wrap break-words ${msg.isDeleted ? 'italic opacity-60' : ''}`}>
                                <LinkifiedText
                                  text={msg.content}
                                  mentionNames={mentionCandidates.map((m) => m?.fullName)}
                                  linkClassName={
                                    isMe
                                      ? 'underline text-brand-700 dark:text-brand-300 font-bold hover:opacity-80'
                                      : 'underline text-blue-600 dark:text-blue-400 font-bold hover:opacity-80'
                                  }
                                />
                              </div>
                            )}

                          {/* Interactive Task Chip if linked to a Task */}
                          {(() => {
                            const taskMatch = msg.content?.match(/\/tasks\/(task-[a-zA-Z0-9_-]+)/);
                            if (!taskMatch) return null;
                            const embeddedTaskId = taskMatch[1];
                            return (
                              <div
                                className={`mt-2 p-2 rounded-xl border flex items-center justify-between gap-2.5 ${
                                  isMe
                                    ? 'bg-black/5 dark:bg-white/10 border-black/10 dark:border-white/15'
                                    : 'bg-surface-50 dark:bg-surface-700/80 border-surface-200 dark:border-surface-600/60 shadow-xs'
                                }`}
                              >
                                <div className="flex items-center space-x-2 min-w-0">
                                  <CheckSquare className="w-3.5 h-3.5 shrink-0 text-brand-600 dark:text-brand-400" />
                                  <span className="text-[11px] font-semibold truncate">Task Linked</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    window.dispatchEvent(
                                      new CustomEvent('open-task-modal', {
                                        detail: { taskId: embeddedTaskId },
                                      })
                                    );
                                  }}
                                  className="px-2.5 py-0.5 rounded-lg text-[10px] font-bold shrink-0 bg-brand-600 hover:bg-brand-700 text-white flex items-center space-x-1 shadow-sm transition-transform active:scale-95"
                                >
                                  <span>Open</span>
                                  <ExternalLink className="w-3 h-3 ml-0.5" />
                                </button>
                              </div>
                            );
                          })()}

                          {/* Timestamp and Read Receipts */}
                          <div
                            className={`flex items-center justify-end space-x-1 mt-1 text-[10px] select-none ${
                              isMe ? 'text-surface-600 dark:text-brand-200/80' : 'text-surface-400'
                            }`}
                          >
                            {msg.isEdited && <span className="italic mr-1">(edited)</span>}
                            <span>{formatMessageTime(msg.createdAt)}</span>
                            {isMe && (
                              // Blue only once EVERY other member has read it; click for who/when
                              <button
                                type="button"
                                onClick={() => setInfoMessage(msg)}
                                title={
                                  msg.isSeen
                                    ? 'Read by everyone — click for details'
                                    : `Delivered${
                                        (msg.readBy || []).length > 0
                                          ? ` · read by ${(msg.readBy || []).length} of ${
                                              msg.recipientCount ?? Math.max(1, (currentChannel?.members?.length || 2) - 1)
                                            }`
                                          : ''
                                      } — click for details`
                                }
                                className="inline-flex items-center hover:opacity-80"
                              >
                                <CheckCheck
                                  className={`w-3.5 h-3.5 ${
                                    msg.isSeen ? 'text-[#53bdeb] stroke-[2.5]' : 'text-surface-400 stroke-[2]'
                                  }`}
                                />
                              </button>
                            )}
                          </div>

                          {/* Desktop Hover Action Bar */}
                          {!msg.isDeleted && (
                            <div
                              className={`absolute -top-3.5 ${
                                isMe ? 'right-2' : 'left-2'
                              } opacity-0 group-hover/row:opacity-100 transition-opacity hidden sm:flex items-center space-x-0.5 bg-white dark:bg-surface-800 shadow-md border border-surface-200 dark:border-surface-700 rounded-full px-1.5 py-0.5 z-20`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() => setReplyingTo(msg)}
                                title="Reply"
                                className="p-1 text-surface-400 hover:text-brand-600"
                              >
                                <Reply className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setForwardingMessage(msg)}
                                title="Forward"
                                className="p-1 text-surface-400 hover:text-brand-600"
                              >
                                <ChevronsRight className="w-3.5 h-3.5" />
                              </button>
                              {isMe && (
                                <button
                                  onClick={() => setInfoMessage(msg)}
                                  title="Message info"
                                  className="p-1 text-surface-400 hover:text-brand-600"
                                >
                                  <Info className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  navigator.clipboard?.writeText(msg.content);
                                }}
                                title="Copy"
                                className="p-1 text-surface-400 hover:text-surface-700"
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleTogglePin(msg.id)}
                                title={msg.isPinned ? 'Unpin message' : 'Pin message'}
                                className={`p-1 ${msg.isPinned ? 'text-amber-600 hover:text-amber-700' : 'text-surface-400 hover:text-amber-600'}`}
                              >
                                {msg.isPinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                              </button>
                              <button
                                onClick={() => handleToggleReaction(msg.id, '👍')}
                                title="React 👍"
                                className="p-1 text-surface-400 hover:scale-125 transition-transform text-xs"
                              >
                                👍
                              </button>
                              <button
                                onClick={() => handleToggleReaction(msg.id, '❤️')}
                                title="React ❤️"
                                className="p-1 text-surface-400 hover:scale-125 transition-transform text-xs"
                              >
                                ❤️
                              </button>
                              {isMe && (
                                <button
                                  onClick={() => {
                                    setEditingMessage(msg);
                                    setEditText(msg.content);
                                    textareaRef.current?.focus();
                                  }}
                                  title="Edit message"
                                  className="p-1 text-surface-400 hover:text-blue-500"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button
                                onClick={() => setDeleteModalMessage(msg)}
                                title="Delete"
                                className="p-1 text-surface-400 hover:text-rose-500"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleCreateTaskFromMessage(msg)}
                                title="Create Task from this message"
                                className="p-1 text-surface-400 hover:text-brand-600"
                              >
                                <CheckSquare className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}

                          {/* Mobile Touch Action Sheet */}
                          {activeMobileMessageId === msg.id && !msg.isDeleted && (
                            <div
                              className="sm:hidden absolute bottom-full mb-2 left-0 right-0 z-30 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 shadow-elevated rounded-2xl p-2 animate-in fade-in zoom-in-95"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-around pb-2 border-b border-surface-100 dark:border-surface-700 text-lg">
                                {['👍', '❤️', '😂', '😮', '😢', '🙏'].map((emoji) => (
                                  <button
                                    key={emoji}
                                    type="button"
                                    onClick={() => {
                                      handleToggleReaction(msg.id, emoji);
                                      setActiveMobileMessageId(null);
                                    }}
                                    className="p-1.5 hover:scale-125 transition-transform"
                                  >
                                    {emoji}
                                  </button>
                                ))}
                              </div>
                              <div className="grid grid-cols-4 gap-1 pt-2 text-[11px] text-surface-700 dark:text-surface-200 text-center font-medium">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setReplyingTo(msg);
                                    setActiveMobileMessageId(null);
                                  }}
                                  className="p-1 hover:text-brand-600 flex flex-col items-center"
                                >
                                  <Reply className="w-4 h-4 mb-0.5" />
                                  <span>Reply</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setForwardingMessage(msg);
                                    setActiveMobileMessageId(null);
                                  }}
                                  className="p-1 hover:text-brand-600 flex flex-col items-center"
                                >
                                  <ChevronsRight className="w-4 h-4 mb-0.5" />
                                  <span>Forward</span>
                                </button>
                                {isMe && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setInfoMessage(msg);
                                      setActiveMobileMessageId(null);
                                    }}
                                    className="p-1 hover:text-brand-600 flex flex-col items-center"
                                  >
                                    <Info className="w-4 h-4 mb-0.5" />
                                    <span>Info</span>
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard?.writeText(msg.content);
                                    setActiveMobileMessageId(null);
                                  }}
                                  className="p-1 hover:text-brand-600 flex flex-col items-center"
                                >
                                  <Copy className="w-4 h-4 mb-0.5" />
                                  <span>Copy</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleTogglePin(msg.id);
                                    setActiveMobileMessageId(null);
                                  }}
                                  className="p-1 hover:text-amber-600 flex flex-col items-center"
                                >
                                  {msg.isPinned ? <PinOff className="w-4 h-4 mb-0.5" /> : <Pin className="w-4 h-4 mb-0.5" />}
                                  <span>{msg.isPinned ? 'Unpin' : 'Pin'}</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleCreateTaskFromMessage(msg);
                                  }}
                                  className="p-1 hover:text-brand-600 flex flex-col items-center"
                                >
                                  <CheckSquare className="w-4 h-4 mb-0.5" />
                                  <span>Task</span>
                                </button>
                                {isMe && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingMessage(msg);
                                      setEditText(msg.content);
                                      setActiveMobileMessageId(null);
                                    }}
                                    className="p-1 hover:text-blue-500 flex flex-col items-center col-span-2"
                                  >
                                    <Edit2 className="w-4 h-4 mb-0.5" />
                                    <span>Edit</span>
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDeleteModalMessage(msg);
                                    setActiveMobileMessageId(null);
                                  }}
                                  className={`p-1 hover:text-rose-500 flex flex-col items-center ${isMe ? 'col-span-2' : 'col-span-4'}`}
                                >
                                  <Trash2 className="w-4 h-4 mb-0.5 text-rose-500" />
                                  <span className="text-rose-500">Delete</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Reaction Badges */}
                        {Array.isArray(msg.reactions) && msg.reactions.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1 px-1">
                            {msg.reactions.map((r, rIdx) => {
                              const hasMyReaction = Boolean(
                                user && Array.isArray(r.userIds) && r.userIds.includes(user.id)
                              );
                              return (
                                <button
                                  key={rIdx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleReaction(msg.id, r.emoji);
                                  }}
                                  className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors flex items-center space-x-1 shadow-xs ${
                                    hasMyReaction
                                      ? 'bg-brand-50 dark:bg-brand-950/40 border-brand-500/40 text-brand-700 dark:text-brand-300 font-bold'
                                      : 'bg-white dark:bg-surface-800 border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-400'
                                  }`}
                                >
                                  <span>{r.emoji}</span>
                                  <span className="font-semibold text-[10px]">{r.count}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}

              <div ref={messagesEndRef} />
            </div>

            {/* Floating Scroll to Bottom Button */}
            {showScrollBottom && (
              <button
                type="button"
                onClick={scrollToBottom}
                className="absolute bottom-20 right-4 sm:right-6 w-9 h-9 rounded-full bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 shadow-elevated flex items-center justify-center text-surface-600 hover:text-brand-600 z-20 transition-all hover:scale-105 active:scale-95"
                title="Scroll to bottom"
              >
                <ChevronDown className="w-4 h-4" />
                {unreadWhileScrolledUp > 0 && (
                  <span className="absolute -top-1 -right-1 px-1.5 py-0.2 rounded-full text-[9px] font-black bg-brand-600 text-white shadow-xs">
                    {unreadWhileScrolledUp}
                  </span>
                )}
              </button>
            )}

            {/* Typing Indicator Bar */}
            {Object.keys(typingUsers).length > 0 && (
              <div className="px-6 py-1 text-[11px] text-surface-400 italic animate-pulse">
                {Object.values(typingUsers).join(', ')} {Object.keys(typingUsers).length === 1 ? 'is' : 'are'} typing...
              </div>
            )}

            {/* Replying Banner */}
            {replyingTo && (
              <div className="px-4 py-2 bg-surface-50 dark:bg-surface-800/80 border-t border-surface-200 dark:border-surface-800 flex items-center justify-between text-xs text-surface-600 dark:text-surface-300">
                <div className="flex items-center space-x-2 truncate">
                  <Reply className="w-4 h-4 text-brand-500 shrink-0" />
                  <span>
                    Replying to <span className="font-bold">{replyingTo.senderName}</span>: "{replyingTo.content.slice(0, 50)}..."
                  </span>
                </div>
                <button
                  onClick={() => setReplyingTo(null)}
                  className="p-1 hover:text-rose-500 text-surface-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Editing Message Banner */}
            {editingMessage && (
              <div className="px-4 py-2 bg-blue-50 dark:bg-blue-950/40 border-t border-blue-200 dark:border-blue-800 flex items-center justify-between text-xs text-blue-900 dark:text-blue-200">
                <div className="flex items-center space-x-2 truncate">
                  <Edit2 className="w-4 h-4 text-blue-500 shrink-0" />
                  <span className="font-bold">Editing message (Press Esc to cancel)</span>
                </div>
                <button
                  onClick={() => {
                    setEditingMessage(null);
                    setInputContent('');
                  }}
                  className="p-1 hover:text-rose-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Staged Attachments & Pasted Screenshots Preview Bar */}
            {(attachedFiles.length > 0 || isUploadingAttachment) && (
              <div className="px-4 py-2.5 border-t border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/40 flex flex-wrap items-center gap-2.5">
                {attachedFiles.map((att) => {
                  const isImg = isImageFile(att.fileName, att.mimeType);
                  const previewUrl = getAttachmentPreviewUrl(att);
                  const downloadUrl = getAttachmentDownloadUrl(att);

                  return isImg ? (
                    <div
                      key={att.fileId}
                      className="group relative flex items-center space-x-2.5 p-1.5 pr-2.5 rounded-xl bg-white dark:bg-surface-700 border border-surface-200 dark:border-surface-600 text-xs shadow-xs"
                    >
                      <div
                        onClick={() =>
                          setActiveLightbox({
                            url: previewUrl,
                            downloadUrl,
                            fileName: att.fileName,
                            senderName: user?.fullName,
                            createdAt: new Date().toISOString(),
                          })
                        }
                        className="relative w-12 h-12 rounded-lg overflow-hidden bg-surface-100 dark:bg-surface-800 border border-surface-200 dark:border-surface-600 cursor-pointer shrink-0"
                        title="Click to preview screenshot"
                      >
                        <img
                          src={previewUrl}
                          alt={att.fileName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="min-w-0 max-w-[140px]">
                        <span className="font-semibold text-surface-800 dark:text-surface-200 truncate block text-[11px]">
                          {att.fileName}
                        </span>
                        <span className="text-[10px] text-surface-400 block">
                          {(att.fileSize / 1024).toFixed(0)} KB • Ready to send
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(att.fileId)}
                        className="p-1 rounded-full hover:bg-rose-50 dark:hover:bg-rose-950/50 hover:text-rose-500 text-surface-400 transition-colors"
                        title="Remove screenshot"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div
                      key={att.fileId}
                      className="flex items-center space-x-2 py-1.5 px-2.5 rounded-xl bg-white dark:bg-surface-700 border border-surface-200 dark:border-surface-600 text-xs shadow-xs"
                    >
                      <FileText className="w-3.5 h-3.5 text-brand-500 shrink-0" />
                      <span className="font-medium text-surface-800 dark:text-surface-200 truncate max-w-[140px]">
                        {att.fileName}
                      </span>
                      <span className="text-[10px] text-surface-400">
                        ({(att.fileSize / 1024).toFixed(0)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(att.fileId)}
                        className="p-0.5 hover:text-rose-500 text-surface-400 transition-colors"
                        title="Remove attachment"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}

                {isUploadingAttachment && (
                  <div className="flex items-center space-x-2 py-2 px-3 rounded-xl bg-white dark:bg-surface-700 border border-brand-200 dark:border-brand-800 text-xs text-brand-600 dark:text-brand-400 shadow-xs animate-pulse">
                    <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                    <span className="font-semibold text-[11px]">Uploading document / attachment...</span>
                  </div>
                )}
              </div>
            )}

            {/* Direct Chat Counterpart Local Time Bar (Matching Screenshot 1) */}
            {(() => {
              if (currentChannel.type !== 'direct') return null;
              const otherUser =
                (currentChannel.members || []).find((m) => m?.id !== user?.id) ||
                (teamMembers || []).find((m) => m?.id !== user?.id);
              if (!otherUser) return null;
              const now = new Date();
              const isNightTime = now.getHours() < 6 || now.getHours() >= 20;
              const timeStr = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });

              return (
                <div
                  onClick={() => openUserProfile(otherUser)}
                  className="px-4 py-1.5 bg-surface-100/70 dark:bg-surface-800/50 border-t border-surface-200/50 dark:border-surface-800/60 text-[11px] text-surface-500 dark:text-surface-400 flex items-center space-x-1.5 cursor-pointer hover:bg-surface-200/50 dark:hover:bg-surface-700/40 transition-colors"
                  title="Click to view profile & lifetime activity"
                >
                  <span className="text-xs">{isNightTime ? '🌙' : '☀️'}</span>
                  <span>
                    It's {timeStr} for <span className="font-semibold text-surface-700 dark:text-surface-300">{otherUser.fullName}</span>.
                  </span>
                </div>
              );
            })()}

            {/* ----------------- COMPOSER TOOLBAR ----------------- */}
            <div className="p-2.5 sm:p-4 pb-safe bg-white dark:bg-surface-900 border-t border-surface-200 dark:border-surface-800 relative">
              {/* Emoji Picker Popover */}
              <EmojiPickerPopover
                isOpen={isEmojiPickerOpen}
                onClose={() => setIsEmojiPickerOpen(false)}
                onSelectEmoji={(emoji) => {
                  setInputContent((prev) => prev + emoji);
                  setIsEmojiPickerOpen(false);
                  textareaRef.current?.focus();
                }}
              />

              {/* Mentions Autocomplete Popup */}
              {mentionQuery !== null && filteredMentionMembers.length > 0 && (
                <div className="absolute bottom-full mb-2 left-2 sm:left-4 w-[calc(100vw-2rem)] sm:w-72 max-w-sm bg-white dark:bg-surface-800 rounded-2xl shadow-elevated border border-surface-200 dark:border-surface-700 p-1.5 z-40 max-h-56 overflow-y-auto">
                  <div className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-surface-400">
                    {currentChannel?.type === 'direct'
                      ? `Mention in this Chat (${filteredMentionMembers.length})`
                      : `Mention in this Group (${filteredMentionMembers.length})`}
                  </div>
                  {filteredMentionMembers.map((member, idx) => (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => insertMention(member)}
                      className={`w-full text-left px-2.5 py-1.5 rounded-xl flex items-center space-x-2.5 text-xs transition-colors ${
                        idx === selectedMentionIndex
                          ? 'bg-brand-50 dark:bg-brand-950/60 text-brand-600 dark:text-brand-300 font-semibold'
                          : 'hover:bg-surface-100 dark:hover:bg-surface-700/60 text-surface-800 dark:text-surface-200'
                      }`}
                    >
                      <Avatar src={member.avatarUrl} name={member.fullName} size="xs" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-semibold flex items-center space-x-1.5">
                          <span>{member.fullName}</span>
                          <span className="text-[10px] text-surface-400 font-normal">
                            @{member.fullName.split(' ')[0]}
                          </span>
                        </div>
                        <div className="text-[10px] text-surface-400 truncate">{member.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {mentionQuery !== null && filteredMentionMembers.length === 0 && mentionQuery.trim().length > 0 && (
                <div className="absolute bottom-full mb-2 left-2 sm:left-4 w-[calc(100vw-2rem)] sm:w-72 max-w-sm bg-white dark:bg-surface-800 rounded-2xl shadow-elevated border border-surface-200 dark:border-surface-700 p-2.5 z-40 text-center">
                  <p className="text-xs font-medium text-surface-700 dark:text-surface-300">
                    No matching members in this {currentChannel?.type === 'direct' ? 'chat' : 'group'}
                  </p>
                  <p className="text-[10px] text-surface-400 dark:text-surface-500 mt-0.5">
                    Only members of this {currentChannel?.type === 'direct' ? 'chat' : 'group'} can be tagged.
                  </p>
                </div>
              )}

              {/* Hidden File / Camera Inputs */}
              <input type="file" ref={fileInputRef} accept="*/*" multiple className="hidden" onChange={handleFileUpload} />
              <input type="file" ref={cameraInputRef} accept="image/*" className="hidden" onChange={handleFileUpload} />

              {/* Voice Recording Active Mode */}
              {isRecordingVoice ? (
                <div className="flex items-center justify-between p-2 rounded-2xl bg-surface-100 dark:bg-surface-800 border border-brand-500/30 animate-pulse">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 rounded-full bg-rose-500 animate-ping" />
                    <span className="font-mono font-bold text-xs text-rose-600 dark:text-rose-400">
                      {Math.floor(recordingSeconds / 60)}:{(recordingSeconds % 60).toString().padStart(2, '0')}
                    </span>
                    <span className="text-xs text-surface-500">Recording voice message...</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={cancelVoiceRecording}
                      className="p-2 rounded-full hover:bg-surface-200 text-rose-500 transition-colors"
                      title="Cancel recording"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={sendVoiceRecording}
                      className="w-9 h-9 rounded-full bg-brand-600 text-white flex items-center justify-center shadow-md active:scale-95 transition-all"
                      title="Send voice message"
                    >
                      <Send className="w-4 h-4 ml-0.5" />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onDrop={handleChatDrop}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDraggingOverChat(true);
                  }}
                  className="flex flex-col bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-2xl p-2 sm:p-2.5 focus-within:ring-2 focus-within:ring-brand-500/20 focus-within:border-brand-500 transition-all shadow-xs relative"
                >
                  {/* Top Drag-Resize Handle for Adjusting Typing Window Height */}
                  <div
                    onMouseDown={startComposerResize}
                    onDoubleClick={() => {
                      setComposerHeight(84);
                      try {
                        localStorage.setItem('pulse_chat_composer_height', '84');
                      } catch {
                        // Ignore storage error
                      }
                    }}
                    title="Drag up or down to resize typing area (double-click to reset)"
                    className="group/resizer -mt-1.5 mb-1 py-1 flex items-center justify-center cursor-ns-resize select-none"
                  >
                    <div className="w-10 h-1 rounded-full bg-surface-300/80 dark:bg-surface-600/80 group-hover/resizer:bg-brand-500 dark:group-hover/resizer:bg-brand-400 group-hover/resizer:w-14 transition-all" />
                  </div>

                  {/* Slash Commands Popover */}
                  {isSlashMenuOpen && filteredSlashCommands.length > 0 && (
                    <div className="absolute bottom-full mb-2 left-0 w-72 bg-white dark:bg-surface-800 rounded-2xl shadow-elevated border border-surface-200 dark:border-surface-700 p-1.5 z-40 max-h-64 overflow-y-auto animate-in slide-in-from-bottom-2">
                      <div className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-surface-400">
                        Slash Commands
                      </div>
                      {filteredSlashCommands.map((item, idx) => {
                        const CmdIcon = item.icon;
                        return (
                          <button
                            key={item.cmd}
                            type="button"
                            onClick={() => executeSlashCommand(item.cmd)}
                            className={`w-full text-left px-2.5 py-1.5 rounded-xl flex items-center space-x-2.5 text-xs transition-colors ${
                              idx === selectedSlashIndex
                                ? 'bg-brand-50 dark:bg-brand-950/60 text-brand-600 dark:text-brand-300 font-semibold'
                                : 'hover:bg-surface-100 dark:hover:bg-surface-700/60 text-surface-800 dark:text-surface-200'
                            }`}
                          >
                            <div className="w-6 h-6 rounded-lg bg-surface-100 dark:bg-surface-700 flex items-center justify-center shrink-0 text-brand-600 dark:text-brand-400">
                              <CmdIcon className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="font-bold text-xs">{item.title}</div>
                              <div className="text-[10px] text-surface-400 truncate">{item.desc}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Textarea */}
                  <textarea
                    ref={textareaRef}
                    rows={3}
                    style={{
                      height: `${composerHeight}px`,
                      minHeight: '36px',
                      maxHeight: '420px',
                    }}
                    onMouseUp={(e) => {
                      const currentH = e.currentTarget.offsetHeight;
                      if (currentH && Math.abs(currentH - composerHeight) > 2) {
                        const clamped = Math.min(420, Math.max(36, currentH));
                        setComposerHeight(clamped);
                        try {
                          localStorage.setItem('pulse_chat_composer_height', String(clamped));
                        } catch {
                          // Ignore storage error
                        }
                      }
                    }}
                    value={inputContent}
                    onPaste={handleChatPaste}
                    onDrop={handleChatDrop}
                    onChange={(e) => {
                      const val = e.target.value;
                      setInputContent(val);

                      // Slash command detection
                      if (val.startsWith('/')) {
                        const match = val.match(/^\/([a-zA-Z0-9_-]*)$/);
                        if (match) {
                          setIsSlashMenuOpen(true);
                          setSlashQuery(match[1]);
                          setSelectedSlashIndex(0);
                        } else {
                          setIsSlashMenuOpen(false);
                        }
                      } else {
                        setIsSlashMenuOpen(false);
                      }

                      // Cursor detection for mentions
                      const cursorPos = e.target.selectionStart || val.length;
                      const textBeforeCursor = val.slice(0, cursorPos);
                      const mentionMatch = textBeforeCursor.match(/@([a-zA-Z0-9_.-]+(?: [a-zA-Z0-9_.-]*)*|)$/);
                      // A multi-word query only stays open while it still fits someone's full name
                      const mentionFitsName =
                        !!mentionMatch &&
                        (!mentionMatch[1].includes(' ') ||
                          mentionCandidates.some((m) =>
                            (m?.fullName || '').toLowerCase().includes(mentionMatch[1].trimEnd().toLowerCase())
                          ));
                      if (mentionMatch && mentionFitsName) {
                        setMentionQuery(mentionMatch[1]);
                        setMentionMatchIndex(mentionMatch.index ?? -1);
                        setSelectedMentionIndex(0);
                      } else {
                        setMentionQuery(null);
                        setMentionMatchIndex(-1);
                      }

                      if (selectedChannelId) {
                        sendTyping(selectedChannelId, true);
                        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                        typingTimeoutRef.current = setTimeout(() => {
                          sendTyping(selectedChannelId, false);
                        }, 2000);
                      }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      editingMessage
                        ? 'Edit message...'
                        : `Message ${displayName || currentChannel.name}... (Paste screenshot or type / for commands)`
                    }
                    className="w-full bg-transparent px-2 py-1.5 text-sm text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none resize-y leading-relaxed overflow-y-auto"
                  />

                  {/* Scheduled Send Preview Banner */}
                  {scheduledSendTime && (
                    <div className="px-3 py-1.5 bg-amber-50 dark:bg-amber-950/50 rounded-xl text-xs text-amber-900 dark:text-amber-200 flex items-center justify-between mb-1 border border-amber-200/80 dark:border-amber-800/80">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        <span>
                          ⏰ Scheduled: <span className="font-bold">{scheduledSendTime}</span>
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            setScheduledSendTime(null);
                            handleSendMessage();
                          }}
                          className="font-bold text-amber-700 dark:text-amber-300 hover:underline text-[11px]"
                        >
                          Send Now
                        </button>
                        <button
                          type="button"
                          onClick={() => setScheduledSendTime(null)}
                          className="p-1 hover:text-rose-500"
                          title="Cancel schedule"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Composer Bottom Controls */}
                  <div className="flex items-center justify-between pt-2 border-t border-surface-200/60 dark:border-surface-700/60 mt-1">
                    {/* Left: Plus Action Menu, Emojis, Attachments, Camera */}
                    <div className="flex items-center space-x-0.5 sm:space-x-1">
                      {/* Plus Action Button */}
                      <div className="relative" ref={composerPlusMenuRef}>
                        <button
                          type="button"
                          onClick={() => setIsComposerPlusMenuOpen(!isComposerPlusMenuOpen)}
                          className={`p-1.5 rounded-full transition-colors ${
                            isComposerPlusMenuOpen
                              ? 'bg-brand-600 text-white shadow-xs'
                              : 'hover:bg-surface-200 dark:hover:bg-surface-700 text-surface-600 dark:text-surface-300'
                          }`}
                          title="Actions / Add (+)"
                        >
                          <Plus className="w-4 h-4" />
                        </button>

                        {isComposerPlusMenuOpen && (
                          <div className="absolute bottom-full left-0 mb-2 w-56 bg-white dark:bg-surface-800 rounded-2xl shadow-elevated border border-surface-200 dark:border-surface-700 p-1.5 z-50 text-xs animate-in slide-in-from-bottom-2">
                            <button
                              type="button"
                              onClick={() => {
                                setIsComposerPlusMenuOpen(false);
                                fileInputRef.current?.click();
                              }}
                              className="w-full text-left px-3 py-2 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-700 text-surface-800 dark:text-surface-200 transition-colors font-medium flex items-center space-x-2.5"
                            >
                              <Paperclip className="w-4 h-4 text-brand-500 shrink-0" />
                              <span>Attach Document / File</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setIsComposerPlusMenuOpen(false);
                                cameraInputRef.current?.click();
                              }}
                              className="w-full text-left px-3 py-2 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-700 text-surface-800 dark:text-surface-200 transition-colors font-medium flex items-center space-x-2.5"
                            >
                              <Camera className="w-4 h-4 text-emerald-500 shrink-0" />
                              <span>Take Photo / Camera</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setIsComposerPlusMenuOpen(false);
                                handleCreateTaskFromChat();
                              }}
                              className="w-full text-left px-3 py-2 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-700 text-surface-800 dark:text-surface-200 transition-colors font-medium flex items-center space-x-2.5"
                            >
                              <CheckSquare className="w-4 h-4 text-amber-500 shrink-0" />
                              <span>Create Task from Chat</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setIsComposerPlusMenuOpen(false);
                                if (currentChannel.type === 'direct' && otherUser) {
                                  startDirectCall(
                                    {
                                      id: otherUser.id,
                                      name: otherUser.fullName,
                                      avatarUrl: otherUser.avatarUrl || undefined,
                                      role: otherUser.title || otherUser.role,
                                    },
                                    false,
                                    currentChannel.id
                                  );
                                } else {
                                  startGroupCall({ id: currentChannel.id, name: currentChannel.name }, false);
                                }
                              }}
                              className="w-full text-left px-3 py-2 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-700 text-surface-800 dark:text-surface-200 transition-colors font-medium flex items-center space-x-2.5"
                            >
                              <PhoneCall className="w-4 h-4 text-teal-500 shrink-0" />
                              <span>Start Audio Call</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setIsComposerPlusMenuOpen(false);
                                if (currentChannel.type === 'direct' && otherUser) {
                                  startDirectCall(
                                    {
                                      id: otherUser.id,
                                      name: otherUser.fullName,
                                      avatarUrl: otherUser.avatarUrl || undefined,
                                      role: otherUser.title || otherUser.role,
                                    },
                                    true,
                                    currentChannel.id
                                  );
                                } else {
                                  startGroupCall({ id: currentChannel.id, name: currentChannel.name }, true);
                                }
                              }}
                              className="w-full text-left px-3 py-2 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-700 text-surface-800 dark:text-surface-200 transition-colors font-medium flex items-center space-x-2.5"
                            >
                              <Video className="w-4 h-4 text-purple-500 shrink-0" />
                              <span>Start Video Call</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setIsComposerPlusMenuOpen(false);
                                setIsScheduleMenuOpen(true);
                              }}
                              className="w-full text-left px-3 py-2 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-700 text-surface-800 dark:text-surface-200 transition-colors font-medium flex items-center space-x-2.5"
                            >
                              <Clock className="w-4 h-4 text-indigo-500 shrink-0" />
                              <span>Schedule Message</span>
                            </button>
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
                        className="p-1.5 rounded-full hover:bg-surface-200 dark:hover:bg-surface-700 text-surface-500 transition-colors"
                        title="Emojis"
                      >
                        <Smile className="w-4 h-4" />
                      </button>

                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploadingAttachment}
                        className="p-1.5 rounded-full hover:bg-surface-200 dark:hover:bg-surface-700 text-surface-500 transition-colors"
                        title="Attach files"
                      >
                        {isUploadingAttachment ? (
                          <Loader2 className="w-4 h-4 animate-spin text-brand-500" />
                        ) : (
                          <Paperclip className="w-4 h-4" />
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => cameraInputRef.current?.click()}
                        disabled={isUploadingAttachment}
                        className="p-1.5 rounded-full hover:bg-surface-200 dark:hover:bg-surface-700 text-surface-500 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
                        title="Take photo"
                      >
                        <Camera className="w-4 h-4" />
                      </button>

                      {/* Scheduled Send Dropdown */}
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setIsScheduleMenuOpen(!isScheduleMenuOpen)}
                          className={`p-1.5 rounded-full transition-colors ${
                            scheduledSendTime
                              ? 'text-brand-600 bg-brand-50 dark:bg-brand-950/60'
                              : 'text-surface-400 hover:text-brand-600 hover:bg-surface-200 dark:hover:bg-surface-700'
                          }`}
                          title="Schedule message / Send later"
                        >
                          <Clock className="w-4 h-4" />
                        </button>

                        {isScheduleMenuOpen && (
                          <div className="absolute bottom-full left-0 mb-2 w-52 bg-white dark:bg-surface-800 rounded-2xl shadow-elevated border border-surface-200 dark:border-surface-700 p-1.5 z-40 text-xs animate-in slide-in-from-bottom-2">
                            <div className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-surface-400">
                              Send Later
                            </div>
                            {[
                              { label: 'Tomorrow at 9:00 AM', time: 'Tomorrow 9:00 AM' },
                              { label: 'This afternoon at 2:00 PM', time: 'Today 2:00 PM' },
                              { label: 'In 1 hour', time: 'In 1 hour' },
                              { label: 'Next Monday at 9:00 AM', time: 'Next Monday 9:00 AM' },
                            ].map((opt) => (
                              <button
                                key={opt.label}
                                type="button"
                                onClick={() => {
                                  setScheduledSendTime(opt.time);
                                  setIsScheduleMenuOpen(false);
                                }}
                                className="w-full text-left px-2.5 py-1.5 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-700 text-surface-800 dark:text-surface-200 transition-colors font-medium flex items-center justify-between"
                              >
                                <span>{opt.label}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: Create Task Button + Send or Mic Button */}
                    <div className="flex items-center space-x-2">
                      <span className="hidden lg:inline text-[10px] text-surface-400 select-none mr-1">
                        Shift+Enter for newline
                      </span>

                      {/* Preserved Hallmark Feature: Create Task from Chat */}
                      <button
                        type="button"
                        onClick={handleCreateTaskFromChat}
                        className="px-2.5 sm:px-3 py-1.5 bg-[#121417] hover:bg-black dark:bg-surface-700 dark:hover:bg-surface-600 text-white rounded-xl font-bold text-xs shadow-xs transition-all flex items-center space-x-1.5 active:scale-95"
                        title="Create Task from Chat (Ctrl + Enter)"
                      >
                        <CheckSquare className="w-3.5 h-3.5 text-brand-400" />
                        <span className="hidden sm:inline">Create Task</span>
                        <span className="sm:hidden text-[11px]">Task</span>
                      </button>

                      {/* Dual Action: Send button when content typed, Mic button when empty */}
                      {inputContent.trim() || attachedFiles.length > 0 || editingMessage ? (
                        <button
                          type="button"
                          onClick={() => handleSendMessage()}
                          className="w-9 h-9 bg-brand-600 hover:bg-brand-700 text-white rounded-full flex items-center justify-center shadow-md active:scale-95 transition-all shrink-0"
                          title="Send message (Enter)"
                        >
                          <Send className="w-4 h-4 ml-0.5" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={startVoiceRecording}
                          className="w-9 h-9 bg-brand-600 hover:bg-brand-700 text-white rounded-full flex items-center justify-center shadow-md active:scale-95 transition-all shrink-0"
                          title="Record voice note"
                        >
                          <Mic className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </>
    );
  })()
) : (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-xs text-surface-400">
            <MessageSquare className="w-12 h-12 text-surface-300 mb-3 stroke-[1.5]" />
            <h3 className="font-bold text-sm text-surface-700 dark:text-surface-300">IHB Pulse Messages</h3>
            <p className="mt-1 max-w-xs text-surface-400">
              Select a conversation from the sidebar or click + to start a new chat.
            </p>
          </div>
        )}
      </div>

      {/* ----------------- DOCKED USER PROFILE DRAWER (CLICKUP CHAT STANDARD) ----------------- */}
      {isProfileDrawerOpen && profileDrawerUser && (
        <UserProfileDrawer
          user={profileDrawerUser}
          onClose={() => setIsProfileDrawerOpen(false)}
          isDocked={true}
          onNavigateToChat={(targetUser) => {
            setIsProfileDrawerOpen(false);
          }}
        />
      )}

      {/* ----------------- GROUP MEMBERS SLIDEOUT DRAWER ----------------- */}
      {isMembersDrawerOpen && currentChannel && currentChannel.type !== 'direct' && (
        <div className="w-80 border-l border-surface-200 dark:border-surface-800 bg-surface-50/50 dark:bg-surface-950/40 flex flex-col shrink-0 animate-slide-left z-20">
          <div className="p-4 border-b border-surface-200 dark:border-surface-800 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-brand-600 dark:text-brand-400" />
              <span className="font-bold text-xs text-surface-900 dark:text-surface-100">
                Channel Members ({(currentChannel.members || []).length})
              </span>
            </div>
            <button
              onClick={() => setIsMembersDrawerOpen(false)}
              className="p-1 hover:text-rose-500 text-surface-400"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-3 border-b border-surface-200 dark:border-surface-800">
            <input
              type="text"
              value={memberSearchQuery}
              onChange={(e) => setMemberSearchQuery(e.target.value)}
              placeholder="Search members..."
              className="w-full px-3 py-1.5 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl text-xs text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {(currentChannel.members || [])
              .filter((m) => {
                if (!memberSearchQuery.trim()) return true;
                const q = memberSearchQuery.toLowerCase();
                return m?.fullName?.toLowerCase().includes(q) || m?.email?.toLowerCase().includes(q);
              })
              .map((m) => (
                <div
                  key={m.id}
                  className="flex items-center justify-between p-2 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 text-xs transition-colors"
                >
                  <div
                    onClick={() => openUserProfile(m)}
                    className="flex items-center space-x-2.5 truncate cursor-pointer hover:text-brand-600 transition-colors"
                    title={`View ${m.fullName}'s profile`}
                  >
                    <Avatar src={m.avatarUrl} name={m.fullName} size="xs" isOnline={isUserOnline(m.id)} userData={m} />
                    <div className="truncate">
                      <span className="font-semibold block truncate">{m.fullName}</span>
                      <span className="text-[10px] text-surface-400 block truncate">{m.title || m.role}</span>
                    </div>
                  </div>
                  {m.id === user?.id ? (
                    <button
                      onClick={() => handleRemoveMemberFromChannel(m.id, m.fullName)}
                      className="text-rose-500 hover:text-rose-600 text-[10px] font-bold"
                    >
                      Leave
                    </button>
                  ) : (
                    (currentChannel.createdBy === user?.id || user?.role === 'admin') && (
                      <button
                        onClick={() => handleRemoveMemberFromChannel(m.id, m.fullName)}
                        className="text-surface-400 hover:text-rose-500 p-1"
                        title="Remove member"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )
                  )}
                </div>
              ))}
          </div>

          <div className="p-3 border-t border-surface-200 dark:border-surface-800">
            <button
              onClick={() => setIsAddMembersModalOpen(true)}
              className="w-full py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-xl text-xs font-bold flex items-center justify-center space-x-1.5 shadow-sm transition-all"
            >
              <UserPlus className="w-4 h-4" />
              <span>Add Colleagues</span>
            </button>
          </div>
        </div>
      )}

      {/* ----------------- MESSAGE INFO MODAL (who read it, and when) ----------------- */}
      <MessageInfoModal
        isOpen={Boolean(infoMessage)}
        onClose={() => setInfoMessage(null)}
        message={infoMessage}
        token={token}
        currentUserId={user?.id}
      />

      {/* ----------------- FORWARD MESSAGE MODAL ----------------- */}
      <ForwardMessageModal
        isOpen={Boolean(forwardingMessage)}
        onClose={() => setForwardingMessage(null)}
        message={forwardingMessage}
        channels={channels}
        currentUser={user}
        onForward={handleForwardMessage}
      />

      {/* ----------------- DELETE MESSAGE MODAL ----------------- */}
      {deleteModalMessage && (
        <Modal isOpen={Boolean(deleteModalMessage)} onClose={() => setDeleteModalMessage(null)} title="Delete Message?">
          <div className="space-y-4">
            <p className="text-xs text-surface-600 dark:text-surface-300">
              Are you sure you want to delete this message?
            </p>
            <div className="flex flex-col space-y-2 pt-2">
              {(deleteModalMessage.senderId === user?.id || user?.role === 'admin') && (
                <button
                  type="button"
                  onClick={() => handleDeleteMessage(deleteModalMessage, true)}
                  className="w-full py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs shadow-sm transition-colors"
                >
                  Delete for Everyone
                </button>
              )}
              <button
                type="button"
                onClick={() => handleDeleteMessage(deleteModalMessage, false)}
                className="w-full py-2.5 rounded-xl bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-800 dark:text-surface-200 font-bold text-xs transition-colors"
              >
                Delete for Me
              </button>
              <button
                type="button"
                onClick={() => setDeleteModalMessage(null)}
                className="w-full py-2 rounded-xl text-surface-500 hover:text-surface-800 text-xs font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ----------------- CREATE NEW CHANNEL MODAL ----------------- */}
      <Modal
        isOpen={isNewChannelModalOpen}
        onClose={() => setIsNewChannelModalOpen(false)}
        title="Start Conversation or Group"
      >
        <form onSubmit={handleCreateChannel} className="space-y-4">
          <div className="flex p-1 bg-surface-100 dark:bg-surface-800 rounded-xl">
            <button
              type="button"
              onClick={() => setNewChannelType('direct')}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                newChannelType === 'direct'
                  ? 'bg-white dark:bg-surface-700 text-brand-600 dark:text-brand-400 shadow-sm'
                  : 'text-surface-600 dark:text-surface-400'
              }`}
            >
              Direct Message
            </button>
            <button
              type="button"
              onClick={() => setNewChannelType('group')}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                newChannelType === 'group'
                  ? 'bg-white dark:bg-surface-700 text-brand-600 dark:text-brand-400 shadow-sm'
                  : 'text-surface-600 dark:text-surface-400'
              }`}
            >
              Group Channel
            </button>
          </div>

          {newChannelType === 'direct' ? (
            <div>
              <label className="block text-xs font-bold text-surface-700 dark:text-surface-300 mb-1">
                Select Colleague
              </label>
              <select
                value={newDirectTargetUserId}
                onChange={(e) => setNewDirectTargetUserId(e.target.value)}
                required
                className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl text-xs focus:ring-1 focus:ring-brand-500"
              >
                <option value="">Choose a team member...</option>
                {teamMembers
                  .filter((m) => m.id !== user?.id)
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.fullName} ({m.email})
                    </option>
                  ))}
              </select>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-bold text-surface-700 dark:text-surface-300 mb-1">
                  Group Name
                </label>
                <input
                  type="text"
                  value={newChannelName}
                  onChange={(e) => setNewChannelName(e.target.value)}
                  placeholder="e.g. Mobile Engineering, Marketing"
                  required
                  className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl text-xs focus:ring-1 focus:ring-brand-500"
                />
              </div>

              {/* Members Selection Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold text-surface-700 dark:text-surface-300">
                    Add Members ({newGroupMemberIds.length} selected)
                  </label>
                  <div className="flex items-center space-x-2 text-[11px]">
                    {teamMembers.filter((m) => m.id !== user?.id).length > 0 && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            const allIds = teamMembers.filter((m) => m.id !== user?.id).map((m) => m.id);
                            setNewGroupMemberIds(allIds);
                          }}
                          className="text-brand-600 dark:text-brand-400 hover:underline font-semibold"
                        >
                          Select all
                        </button>
                        <span className="text-surface-300 dark:text-surface-600">|</span>
                        <button
                          type="button"
                          onClick={() => setNewGroupMemberIds([])}
                          className="text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 font-semibold"
                        >
                          Clear
                        </button>
                        <span className="text-surface-300 dark:text-surface-600">|</span>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setIsQuickInviteOpen((prev) => !prev);
                        if (!isQuickInviteOpen && newGroupMemberSearch.trim()) {
                          if (newGroupMemberSearch.includes('@')) {
                            setQuickInviteEmail(newGroupMemberSearch);
                            setQuickInviteName(newGroupMemberSearch.split('@')[0]);
                          } else {
                            setQuickInviteName(newGroupMemberSearch);
                            setQuickInviteEmail('');
                          }
                        }
                      }}
                      className="text-brand-600 hover:text-brand-700 dark:text-brand-400 font-bold flex items-center space-x-1"
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      <span>Invite New</span>
                    </button>
                  </div>
                </div>

                {/* Selected Member Badges Pills */}
                {newGroupMemberIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 p-2 bg-surface-50 dark:bg-surface-800/60 rounded-xl border border-surface-200 dark:border-surface-700/60 max-h-24 overflow-y-auto">
                    {teamMembers
                      .filter((m) => newGroupMemberIds.includes(m.id))
                      .map((member) => (
                        <span
                          key={member.id}
                          className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full text-[11px] font-semibold bg-brand-50 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 border border-brand-200 dark:border-brand-700/50"
                        >
                          <Avatar src={member.avatarUrl} name={member.fullName} size="xs" />
                          <span className="max-w-[120px] truncate">{member.fullName}</span>
                          <button
                            type="button"
                            onClick={() => setNewGroupMemberIds(newGroupMemberIds.filter((id) => id !== member.id))}
                            className="text-brand-500 hover:text-brand-800 dark:hover:text-brand-100 ml-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                  </div>
                )}

                {/* Inline Quick Invite Form */}
                {isQuickInviteOpen && (
                  <div className="p-3 bg-brand-50/70 dark:bg-brand-950/40 border border-brand-200 dark:border-brand-800/80 rounded-xl space-y-2.5 animate-in fade-in duration-150">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-brand-900 dark:text-brand-200 flex items-center space-x-1.5">
                        <UserPlus className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
                        <span>Invite Colleague to Workspace</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setIsQuickInviteOpen(false)}
                        className="text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 p-0.5"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] font-bold text-surface-600 dark:text-surface-400 mb-0.5">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={quickInviteName}
                          onChange={(e) => setQuickInviteName(e.target.value)}
                          placeholder="e.g. Ahmed Khan"
                          className="w-full px-2.5 py-1.5 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-surface-600 dark:text-surface-400 mb-0.5">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          value={quickInviteEmail}
                          onChange={(e) => setQuickInviteEmail(e.target.value)}
                          placeholder="ahmed@example.com"
                          required
                          className="w-full px-2.5 py-1.5 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg text-xs"
                        />
                      </div>
                    </div>

                    {quickInviteError && (
                      <p className="text-[11px] text-rose-500 font-medium">{quickInviteError}</p>
                    )}

                    <div className="flex justify-end space-x-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setIsQuickInviteOpen(false)}
                        className="px-3 py-1 text-xs text-surface-500 hover:bg-surface-200/50 rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleQuickInviteMember('newChannel')}
                        disabled={isSubmittingQuickInvite}
                        className="px-3.5 py-1.5 text-xs font-bold bg-brand-600 hover:bg-brand-700 text-white rounded-lg shadow-xs flex items-center space-x-1 disabled:opacity-50"
                      >
                        {isSubmittingQuickInvite ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                        <span>{isSubmittingQuickInvite ? 'Inviting...' : 'Invite & Select'}</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Member Search Bar */}
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={newGroupMemberSearch}
                    onChange={(e) => setNewGroupMemberSearch(e.target.value)}
                    placeholder="Search colleagues by name or email..."
                    className="w-full pl-8 pr-8 py-2 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl text-xs focus:ring-1 focus:ring-brand-500"
                  />
                  {newGroupMemberSearch && (
                    <button
                      type="button"
                      onClick={() => setNewGroupMemberSearch('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>

                {/* Candidate Member List */}
                <div className="max-h-56 overflow-y-auto space-y-1 border border-surface-200 dark:border-surface-700 rounded-xl p-1.5 bg-surface-50/30 dark:bg-surface-900/40">
                  {isLoadingTeamMembers ? (
                    <div className="py-6 text-center text-xs text-surface-400 flex items-center justify-center space-x-2">
                      <Loader2 className="w-4 h-4 animate-spin text-brand-500" />
                      <span>Loading team members...</span>
                    </div>
                  ) : (() => {
                    const colleagues = teamMembers.filter((m) => m.id !== user?.id);
                    const q = newGroupMemberSearch.trim().toLowerCase();
                    const filtered = colleagues.filter(
                      (m) =>
                        (m.fullName || '').toLowerCase().includes(q) ||
                        (m.email || '').toLowerCase().includes(q)
                    );

                    if (colleagues.length === 0) {
                      return (
                        <div className="py-6 px-4 text-center">
                          <Users className="w-8 h-8 text-surface-300 dark:text-surface-600 mx-auto mb-2" />
                          <p className="font-bold text-xs text-surface-700 dark:text-surface-300">
                            No other team members in workspace yet
                          </p>
                          <p className="text-[11px] text-surface-400 mt-0.5 mb-3">
                            Invite your colleagues to start group channels and chats.
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              setIsQuickInviteOpen(true);
                              setQuickInviteName(newGroupMemberSearch);
                              setQuickInviteEmail('');
                            }}
                            className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white rounded-xl text-xs font-bold shadow-xs transition-all"
                          >
                            <UserPlus className="w-3.5 h-3.5" />
                            <span>Invite Colleague</span>
                          </button>
                        </div>
                      );
                    }

                    if (filtered.length === 0) {
                      return (
                        <div className="py-6 px-4 text-center bg-white dark:bg-surface-800 rounded-lg border border-dashed border-surface-200 dark:border-surface-700">
                          <UserPlus className="w-7 h-7 text-brand-500 mx-auto mb-1.5 opacity-80" />
                          <p className="font-bold text-xs text-surface-800 dark:text-surface-200">
                            No member found matching "{newGroupMemberSearch}"
                          </p>
                          <p className="text-[11px] text-surface-400 mt-0.5 mb-2.5">
                            Invite "{newGroupMemberSearch}" to your workspace to add them to this group.
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              setIsQuickInviteOpen(true);
                              if (newGroupMemberSearch.includes('@')) {
                                setQuickInviteEmail(newGroupMemberSearch);
                                setQuickInviteName(newGroupMemberSearch.split('@')[0]);
                              } else {
                                setQuickInviteName(newGroupMemberSearch);
                                setQuickInviteEmail('');
                              }
                            }}
                            className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white rounded-xl text-xs font-bold shadow-xs transition-all"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            <span>Invite "{newGroupMemberSearch}" & Add</span>
                          </button>
                        </div>
                      );
                    }

                    return filtered.map((m) => {
                      const isSelected = newGroupMemberIds.includes(m.id);
                      return (
                        <div
                          key={m.id}
                          onClick={() => {
                            if (isSelected) {
                              setNewGroupMemberIds(newGroupMemberIds.filter((id) => id !== m.id));
                            } else {
                              setNewGroupMemberIds([...newGroupMemberIds, m.id]);
                            }
                          }}
                          className={`flex items-center justify-between p-2 rounded-xl cursor-pointer transition-colors text-xs ${
                            isSelected
                              ? 'bg-brand-50 dark:bg-brand-950/40 text-brand-900 dark:text-brand-200 border border-brand-200/80 dark:border-brand-800/60'
                              : 'hover:bg-white dark:hover:bg-surface-800 text-surface-700 dark:text-surface-200 border border-transparent'
                          }`}
                        >
                          <div className="flex items-center space-x-2.5 min-w-0">
                            <Avatar src={m.avatarUrl} name={m.fullName} size="sm" isOnline={isUserOnline(m.id)} userData={m} />
                            <div className="truncate">
                              <div className="font-semibold truncate leading-tight">
                                {m.fullName}
                              </div>
                              {m.email && (
                                <div className="text-[11px] text-surface-400 dark:text-surface-500 truncate leading-tight">
                                  {m.email} {m.title ? `• ${m.title}` : ''}
                                </div>
                              )}
                            </div>
                          </div>
                          <div
                            className={`w-4 h-4 rounded border flex items-center justify-center transition-colors shrink-0 ${
                              isSelected
                                ? 'bg-brand-600 border-brand-600 text-white'
                                : 'border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-700'
                            }`}
                          >
                            {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </>
          )}

          <div className="pt-2 flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsNewChannelModalOpen(false)}
              className="px-4 py-2 text-xs font-medium text-surface-600 hover:bg-surface-100 rounded-xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 text-xs font-bold bg-brand-600 hover:bg-brand-700 text-white rounded-xl shadow-sm"
            >
              Create Chat
            </button>
          </div>
        </form>
      </Modal>

      {/* ----------------- ADD MEMBERS MODAL ----------------- */}
      <Modal
        isOpen={isAddMembersModalOpen}
        onClose={() => setIsAddMembersModalOpen(false)}
        title={`Add Members to ${currentChannel?.name || 'Channel'}`}
      >
        <form onSubmit={handleAddMembersToChannel} className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-surface-500">
              Select colleagues to add to this group channel
            </span>
            <button
              type="button"
              onClick={() => {
                setIsQuickInviteOpen((prev) => !prev);
                if (!isQuickInviteOpen && addMemberSearchQuery.trim()) {
                  if (addMemberSearchQuery.includes('@')) {
                    setQuickInviteEmail(addMemberSearchQuery);
                    setQuickInviteName(addMemberSearchQuery.split('@')[0]);
                  } else {
                    setQuickInviteName(addMemberSearchQuery);
                    setQuickInviteEmail('');
                  }
                }
              }}
              className="text-brand-600 hover:text-brand-700 dark:text-brand-400 font-bold text-xs flex items-center space-x-1"
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span>Invite New</span>
            </button>
          </div>

          {/* Inline Quick Invite Form */}
          {isQuickInviteOpen && (
            <div className="p-3 bg-brand-50/70 dark:bg-brand-950/40 border border-brand-200 dark:border-brand-800/80 rounded-xl space-y-2.5 animate-in fade-in duration-150">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-brand-900 dark:text-brand-200 flex items-center space-x-1.5">
                  <UserPlus className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
                  <span>Invite Colleague to Workspace & Channel</span>
                </span>
                <button
                  type="button"
                  onClick={() => setIsQuickInviteOpen(false)}
                  className="text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 p-0.5"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-bold text-surface-600 dark:text-surface-400 mb-0.5">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={quickInviteName}
                    onChange={(e) => setQuickInviteName(e.target.value)}
                    placeholder="e.g. Ahmed Khan"
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-surface-600 dark:text-surface-400 mb-0.5">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={quickInviteEmail}
                    onChange={(e) => setQuickInviteEmail(e.target.value)}
                    placeholder="ahmed@example.com"
                    required
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg text-xs"
                  />
                </div>
              </div>

              {quickInviteError && (
                <p className="text-[11px] text-rose-500 font-medium">{quickInviteError}</p>
              )}

              <div className="flex justify-end space-x-2 pt-1">
                <button
                  type="button"
                  onClick={() => setIsQuickInviteOpen(false)}
                  className="px-3 py-1 text-xs text-surface-500 hover:bg-surface-200/50 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickInviteMember('addMembers')}
                  disabled={isSubmittingQuickInvite}
                  className="px-3.5 py-1.5 text-xs font-bold bg-brand-600 hover:bg-brand-700 text-white rounded-lg shadow-xs flex items-center space-x-1 disabled:opacity-50"
                >
                  {isSubmittingQuickInvite ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  <span>{isSubmittingQuickInvite ? 'Inviting...' : 'Invite & Select'}</span>
                </button>
              </div>
            </div>
          )}

          {/* Search Input with Clear Button */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={addMemberSearchQuery}
              onChange={(e) => setAddMemberSearchQuery(e.target.value)}
              placeholder="Search colleagues by name or email..."
              className="w-full pl-8 pr-8 py-2 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl text-xs focus:ring-1 focus:ring-brand-500"
            />
            {addMemberSearchQuery && (
              <button
                type="button"
                onClick={() => setAddMemberSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Candidate List */}
          <div className="max-h-56 overflow-y-auto space-y-1 border border-surface-200 dark:border-surface-700 rounded-xl p-1.5 bg-surface-50/30 dark:bg-surface-900/40">
            {isLoadingTeamMembers ? (
              <div className="py-6 text-center text-xs text-surface-400 flex items-center justify-center space-x-2">
                <Loader2 className="w-4 h-4 animate-spin text-brand-500" />
                <span>Loading team members...</span>
              </div>
            ) : (() => {
              const availableColleagues = teamMembers.filter(
                (m) => !(currentChannel?.members || []).some((cm) => cm?.id === m.id)
              );
              const q = addMemberSearchQuery.trim().toLowerCase();
              const filtered = availableColleagues.filter((m) => {
                if (!q) return true;
                return (
                  (m.fullName || '').toLowerCase().includes(q) ||
                  (m.email || '').toLowerCase().includes(q)
                );
              });

              if (availableColleagues.length === 0) {
                return (
                  <div className="py-6 px-4 text-center">
                    <Users className="w-8 h-8 text-surface-300 dark:text-surface-600 mx-auto mb-2" />
                    <p className="font-bold text-xs text-surface-700 dark:text-surface-300">
                      All workspace members are already in this channel
                    </p>
                    <p className="text-[11px] text-surface-400 mt-0.5 mb-3">
                      Invite a new colleague to your workspace to add them to this group.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setIsQuickInviteOpen(true);
                        setQuickInviteName(addMemberSearchQuery);
                        setQuickInviteEmail('');
                      }}
                      className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white rounded-xl text-xs font-bold shadow-xs transition-all"
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      <span>Invite New Colleague</span>
                    </button>
                  </div>
                );
              }

              if (filtered.length === 0) {
                return (
                  <div className="py-6 px-4 text-center bg-white dark:bg-surface-800 rounded-lg border border-dashed border-surface-200 dark:border-surface-700">
                    <UserPlus className="w-7 h-7 text-brand-500 mx-auto mb-1.5 opacity-80" />
                    <p className="font-bold text-xs text-surface-800 dark:text-surface-200">
                      No colleague found matching "{addMemberSearchQuery}"
                    </p>
                    <p className="text-[11px] text-surface-400 mt-0.5 mb-2.5">
                      Invite "{addMemberSearchQuery}" to your workspace to add them to this channel.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setIsQuickInviteOpen(true);
                        if (addMemberSearchQuery.includes('@')) {
                          setQuickInviteEmail(addMemberSearchQuery);
                          setQuickInviteName(addMemberSearchQuery.split('@')[0]);
                        } else {
                          setQuickInviteName(addMemberSearchQuery);
                          setQuickInviteEmail('');
                        }
                      }}
                      className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white rounded-xl text-xs font-bold shadow-xs transition-all"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Invite "{addMemberSearchQuery}" & Add</span>
                    </button>
                  </div>
                );
              }

              return filtered.map((m) => {
                const isSelected = selectedNewMembers.includes(m.id);
                return (
                  <div
                    key={m.id}
                    onClick={() => {
                      if (isSelected) setSelectedNewMembers(selectedNewMembers.filter((id) => id !== m.id));
                      else setSelectedNewMembers([...selectedNewMembers, m.id]);
                    }}
                    className={`flex items-center justify-between p-2 rounded-xl cursor-pointer transition-colors text-xs ${
                      isSelected
                        ? 'bg-brand-50 dark:bg-brand-950/40 text-brand-900 dark:text-brand-200 border border-brand-200/80 dark:border-brand-800/60'
                        : 'hover:bg-white dark:hover:bg-surface-800 text-surface-700 dark:text-surface-200 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center space-x-2.5 min-w-0">
                      <Avatar src={m.avatarUrl} name={m.fullName} size="sm" isOnline={isUserOnline(m.id)} userData={m} />
                      <div className="truncate">
                        <span className="font-semibold block truncate leading-tight">
                          {m.fullName}
                        </span>
                        {m.email && (
                          <span className="text-[11px] text-surface-400 dark:text-surface-500 block truncate leading-tight">
                            {m.email} {m.title ? `• ${m.title}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center transition-colors shrink-0 ${
                        isSelected
                          ? 'bg-brand-600 border-brand-600 text-white'
                          : 'border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-700'
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-xs font-semibold text-surface-500">
              {selectedNewMembers.length} selected
            </span>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setIsAddMembersModalOpen(false)}
                className="px-4 py-2 text-xs font-medium text-surface-600 hover:bg-surface-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={selectedNewMembers.length === 0 || isAddingMembers}
                className="px-5 py-2 text-xs font-bold bg-brand-600 hover:bg-brand-700 disabled:opacity-40 text-white rounded-xl shadow-sm flex items-center space-x-1.5"
              >
                {isAddingMembers ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                <span>{isAddingMembers ? 'Adding...' : `Add Selected (${selectedNewMembers.length})`}</span>
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* ----------------- CREATE TASK MODAL (IHB Pulse Hallmark) ----------------- */}
      <CreateTaskModal
        isOpen={isTaskModalOpen}
        onClose={() => setIsTaskModalOpen(false)}
        onTaskCreated={handleChatTaskCreated}
        initialTitle={draftTaskTitle}
        initialDescription={draftTaskDescription}
        initialAttachments={attachedFiles}
        projectId={currentChannel?.projectId || undefined}
        groupMembers={currentChannel?.members}
        initialAssigneeId={
          currentChannel?.type === 'direct'
            ? (currentChannel.members || []).find((m) => m?.id !== user?.id)?.id
            : undefined
        }
      />

      {/* ----------------- WHATSAPP FULLSCREEN IMAGE LIGHTBOX ----------------- */}
      {activeLightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex flex-col animate-in fade-in duration-150"
          onClick={() => setActiveLightbox(null)}
        >
          <div
            className="h-16 px-4 sm:px-6 bg-black/60 border-b border-white/10 flex items-center justify-between text-white shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center space-x-3 min-w-0">
              <button
                type="button"
                onClick={() => setActiveLightbox(null)}
                className="p-2 -ml-2 rounded-full hover:bg-white/10 text-white transition-colors"
                title="Back to conversation"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div className="truncate">
                <h4 className="font-bold text-sm text-white truncate">{activeLightbox.fileName}</h4>
                <span className="text-[11px] text-white/60">
                  {activeLightbox.senderName ? `${activeLightbox.senderName} • ` : ''}
                  {formatMessageTime(activeLightbox.createdAt)}
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <a
                href={activeLightbox.downloadUrl || activeLightbox.url}
                download={activeLightbox.fileName}
                className="p-2 rounded-full hover:bg-white/10 text-white transition-colors flex items-center space-x-1"
                title="Download original file"
              >
                <Download className="w-5 h-5" />
              </a>
              <button
                type="button"
                onClick={() => setActiveLightbox(null)}
                className="p-2 rounded-full hover:bg-white/10 text-white transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div
            className="flex-1 flex items-center justify-center p-3 sm:p-6 overflow-hidden select-none"
            onClick={() => setActiveLightbox(null)}
          >
            <img
              src={activeLightbox.url}
              alt={activeLightbox.fileName}
              className="max-h-[85vh] max-w-[95vw] object-contain rounded-lg shadow-2xl transition-all"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* ----------------- SCHEDULE 1-ON-1 / MEETING MODAL (CLICKUP CHAT STANDARD) ----------------- */}
      <Modal
        isOpen={isScheduleMeetingModalOpen}
        onClose={() => setIsScheduleMeetingModalOpen(false)}
        title="Schedule 1-on-1 or SyncUp Meeting"
      >
        <form onSubmit={handleScheduleMeetingSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-surface-700 dark:text-surface-300 mb-1">
              Meeting Title
            </label>
            <input
              type="text"
              value={meetingModalTitle}
              onChange={(e) => setMeetingModalTitle(e.target.value)}
              placeholder="e.g. 1-on-1 Sync, Project Alignment, Coffee Chat"
              required
              className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl text-xs focus:ring-1 focus:ring-brand-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-surface-700 dark:text-surface-300 mb-1">
                Date
              </label>
              <input
                type="date"
                value={meetingModalDate}
                onChange={(e) => setMeetingModalDate(e.target.value)}
                required
                className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl text-xs focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-surface-700 dark:text-surface-300 mb-1">
                Time
              </label>
              <input
                type="time"
                value={meetingModalTime}
                onChange={(e) => setMeetingModalTime(e.target.value)}
                required
                className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl text-xs focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-surface-700 dark:text-surface-300 mb-1">
              Duration
            </label>
            <select
              value={meetingModalDuration}
              onChange={(e) => setMeetingModalDuration(e.target.value)}
              className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl text-xs focus:ring-1 focus:ring-brand-500"
            >
              <option value="15">15 minutes (Quick Sync)</option>
              <option value="30">30 minutes (Standard 1-on-1)</option>
              <option value="45">45 minutes (Detailed Alignment)</option>
              <option value="60">60 minutes (Comprehensive Session)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-surface-700 dark:text-surface-300 mb-1">
              Agenda / Notes (Optional)
            </label>
            <textarea
              rows={2}
              value={meetingModalDesc}
              onChange={(e) => setMeetingModalDesc(e.target.value)}
              placeholder="Discuss deliverables, roadmap priorities, or blocker resolution..."
              className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl text-xs focus:ring-1 focus:ring-brand-500"
            />
          </div>

          <div className="pt-2 flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsScheduleMeetingModalOpen(false)}
              className="px-4 py-2 text-xs font-medium text-surface-600 hover:bg-surface-100 rounded-xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSchedulingMeeting}
              className="px-5 py-2 text-xs font-bold bg-brand-600 hover:bg-brand-700 text-white rounded-xl shadow-sm flex items-center space-x-1.5 disabled:opacity-50"
            >
              {isSchedulingMeeting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              <span>Schedule & Notify</span>
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
